document.addEventListener('DOMContentLoaded', async function () {
  // Wait for all stylesheets to be fully loaded before measuring layout.
  // DOMContentLoaded fires after HTML parsing but CSS may still be loading,
  // causing getFlipbookDimensions() to measure incorrect values on first visit.
  await new Promise(function(resolve) {
    if (document.readyState === 'complete') {
      resolve();
      return;
    }
    window.addEventListener('load', resolve, { once: true });
  });

  // Additional frame delay to ensure CSS is painted and layout is stable
  await new Promise(function(resolve) {
    requestAnimationFrame(function() {
      requestAnimationFrame(resolve);
    });
  });

  // Resolves once #reader-content is actually visible (clientWidth > 0).
  // On the deep-link path (?mode=read-only) the reader is already visible, so this
  // resolves synchronously. On the click-from-mode-selection path, #book-reader is
  // display:none until the user picks a mode — measuring then would lock the flipbook
  // at the 280px fallback. ResizeObserver fires the moment .active flips display to flex.
  function waitForReaderVisible() {
    return new Promise(function(resolve) {
      var rc = document.getElementById('reader-content');
      if (!rc) { resolve(); return; }
      if (rc.clientWidth > 0) { resolve(); return; }

      if (typeof ResizeObserver !== 'undefined') {
        var ro = new ResizeObserver(function(entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].contentRect.width > 0) { ro.disconnect(); resolve(); return; }
          }
        });
        ro.observe(rc);
        return;
      }

      (function poll() {
        if (rc.clientWidth > 0) { resolve(); return; }
        requestAnimationFrame(poll);
      })();
    });
  }

  // ========== SECTION: Timestamp Parsing & Mode Detection ==========
  /**
   * Parse timestamp string to seconds
   * @param {string} timestamp - Format: "MM:SS" or "HH:MM:SS"
   * @returns {number} - Total seconds, or 0 if invalid
   */
  function parseTimestamp(timestamp) {
    if (!timestamp || typeof timestamp !== 'string') {
      return 0;
    }

    // Remove whitespace
    timestamp = timestamp.trim();

    // Split by colon
    const parts = timestamp.split(':');

    // Validate format
    if (parts.length < 2 || parts.length > 3) {
      console.warn('Invalid timestamp format:', timestamp);
      return 0;
    }

    try {
      let hours = 0;
      let minutes = 0;
      let seconds = 0;

      if (parts.length === 2) {
        // MM:SS format
        minutes = parseInt(parts[0], 10);
        seconds = parseInt(parts[1], 10);
      } else if (parts.length === 3) {
        // HH:MM:SS format
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
        seconds = parseInt(parts[2], 10);
      }

      // Validate ranges
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        console.warn('Invalid timestamp values:', timestamp);
        return 0;
      }

      if (minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60 || hours < 0) {
        console.warn('Timestamp values out of range:', timestamp);
        return 0;
      }

      // Calculate total seconds
      const totalSeconds = hours * 3600 + minutes * 60 + seconds;
      return totalSeconds;
    } catch (e) {
      console.warn('Error parsing timestamp:', timestamp, e);
      return 0;
    }
  }

  // Check mode from URL
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode');
  const isReadOnly = mode === 'read-only';

  //   console.log('Current mode:', mode);
  //   console.log('Is Read Only:', isReadOnly);

  // Hide audio player in read-only mode
  const audioMiniEl = document.getElementById('audio-mini');
  const audioExpandedEl = document.getElementById('audio-expanded');
  const readerFooter = document.querySelector('.reader-footer');

  //   console.log('Audio Mini element:', audioMiniEl);
  //   console.log('Audio Expanded element:', audioExpandedEl);

  if (isReadOnly) {
    //     console.log('Hiding audio player for read-only mode');
    if (audioMiniEl) {
      audioMiniEl.style.display = 'none';
      //       console.log('Audio mini hidden');
    }
    if (audioExpandedEl) {
      audioExpandedEl.style.display = 'none';
      //       console.log('Audio expanded hidden');
    }
    if (readerFooter) {
      readerFooter.classList.remove('has-audio');
      //       console.log('Removed has-audio class from footer');
    }
  }

  // Parse product description and split by H6 headings (Page numbers)
  // ========== SECTION: Source Loading & Page Splitting ==========
  const rawContent = document.getElementById('product-description-raw');
  let pages = [];

  // ========== SECTION: Ebook IndexedDB Cache ==========
  // Caches the 24 MB B2 ebook locally so each user only downloads it once.
  // Key = the URL with all query params stripped (presigned URLs change every regen,
  // but the path stays stable). Cache lives 7 days, then refetches.
  // Background HEAD-check refreshes when the underlying file changes (best-effort —
  // if HEAD is CORS-blocked, time-based invalidation kicks in instead).
  const EBOOK_CACHE_DB = 'wishful-ebook-cache';
  const EBOOK_CACHE_STORE = 'books';
  const EBOOK_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  function stripQueryString(url) {
    try { return url.split('?')[0]; } catch (_) { return url; }
  }

  function openCacheDB() {
    return new Promise(function (resolve, reject) {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('indexedDB unavailable'));
        return;
      }
      const req = indexedDB.open(EBOOK_CACHE_DB, 1);
      req.onupgradeneeded = function () {
        const db = req.result;
        if (!db.objectStoreNames.contains(EBOOK_CACHE_STORE)) {
          db.createObjectStore(EBOOK_CACHE_STORE, { keyPath: 'key' });
        }
      };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
    });
  }

  async function cacheGet(key) {
    try {
      const db = await openCacheDB();
      return await new Promise(function (resolve, reject) {
        const tx = db.transaction(EBOOK_CACHE_STORE, 'readonly');
        const req = tx.objectStore(EBOOK_CACHE_STORE).get(key);
        req.onsuccess = function () { resolve(req.result || null); };
        req.onerror = function () { reject(req.error); };
      });
    } catch (e) {
      console.warn('[Ebook] cacheGet failed:', e.message);
      return null;
    }
  }

  async function cachePut(entry) {
    try {
      const db = await openCacheDB();
      return await new Promise(function (resolve, reject) {
        const tx = db.transaction(EBOOK_CACHE_STORE, 'readwrite');
        const req = tx.objectStore(EBOOK_CACHE_STORE).put(entry);
        req.onsuccess = function () { resolve(); };
        req.onerror = function () { reject(req.error); };
      });
    } catch (e) {
      console.warn('[Ebook] cachePut failed:', e.message);
    }
  }

  async function refreshIfChanged(url, cacheKey, cachedEtag) {
    try {
      const head = await fetch(url, { method: 'HEAD' });
      if (!head.ok) return;
      const newEtag = head.headers.get('etag');
      if (!newEtag || newEtag === cachedEtag) {
        console.log('[Ebook] Background freshness check: cache still current');
        return;
      }
      console.log('[Ebook] Background freshness check: ETag changed, refetching…');
      const r = await fetch(url);
      if (!r.ok) return;
      const text = await r.text();
      if (!text || !text.trim()) return;
      await cachePut({
        key: cacheKey,
        html: text,
        etag: newEtag,
        cachedAt: Date.now(),
        byteLength: text.length,
      });
      console.log('[Ebook] Cache updated in background — refresh page to see changes');
    } catch (e) {
      // HEAD often CORS-blocked on B2 presigned URLs — silent fall-through.
    }
  }

  async function loadSourceHtml() {
    if (!rawContent) {
      console.warn('[Ebook] Source element missing (#product-description-raw)');
      return { html: '', fromUrl: false };
    }

    let ebookUrl = rawContent.getAttribute('data-ebook-url');
    // Strip ALL whitespace — guards against accidental space/tab/newline pasted into metafield.
    if (ebookUrl) ebookUrl = ebookUrl.replace(/\s/g, '');
    const fallbackHtml = rawContent.innerHTML || '';

    if (!ebookUrl) {
      console.log('[Ebook v2] Source URL: none (custom.ebook_url not set)');
      console.log('[Ebook v2] Using fallback product description HTML');
      return { html: fallbackHtml, fromUrl: false };
    }

    if (ebookUrl.startsWith('//')) {
      ebookUrl = window.location.protocol + ebookUrl;
    }

    console.log('[Ebook v2] Source URL:', ebookUrl);

    const cacheKey = stripQueryString(ebookUrl);
    const cached = await cacheGet(cacheKey);
    const now = Date.now();
    if (cached && cached.html && now - cached.cachedAt < EBOOK_CACHE_TTL_MS) {
      const ageSec = Math.round((now - cached.cachedAt) / 1000);
      console.log('[Ebook] Cache hit (' + cached.byteLength + ' bytes, ' + ageSec + 's old)');
      // Schedule background freshness check — don't await, don't block initial render.
      setTimeout(function () { refreshIfChanged(ebookUrl, cacheKey, cached.etag); }, 0);
      return { html: cached.html, fromUrl: true };
    }
    if (cached) {
      const ageHr = Math.round((now - cached.cachedAt) / 3600000);
      console.log('[Ebook] Cache stale (' + ageHr + 'h old, > 7 days), refetching');
    } else {
      console.log('[Ebook] Cache miss, fetching…');
    }

    // Inline bookId derivation — loadSourceHtml runs before module-scope `bookId`
    // is declared, so we can't call getBookContext() from these early failure paths.
    function _earlyBookId() {
      try { return window.location.pathname.split('/').pop() || 'unknown'; }
      catch (_) { return 'unknown'; }
    }
    function _emitOpenError(message) {
      try {
        if (typeof gtag === 'function') {
          gtag('event', 'book_open_error', { book_id: _earlyBookId(), error_message: String(message).slice(0, 200) });
        }
      } catch (_) { /* analytics never blocks */ }
    }

    try {
      const response = await fetch(ebookUrl);
      console.log('[Ebook] Fetch status:', response.status);
      if (!response.ok) {
        console.warn('[Ebook] Fetch failed, using fallback product description HTML');
        _emitOpenError('fetch_not_ok_' + response.status);
        return { html: fallbackHtml, fromUrl: false };
      }

      const text = await response.text();
      if (text && text.trim()) {
        console.log('[Ebook] Using remote ebook file (' + text.length + ' bytes)');
        const etag = response.headers.get('etag');
        // Fire-and-forget cache write — don't block the reader on storage I/O.
        cachePut({
          key: cacheKey,
          html: text,
          etag: etag,
          cachedAt: Date.now(),
          byteLength: text.length,
        }).then(function () {
          console.log('[Ebook] Cache stored (' + text.length + ' bytes, etag=' + (etag || 'none') + ')');
        });
        return { html: text, fromUrl: true };
      }

      console.warn('[Ebook] Remote ebook file empty, using fallback product description HTML');
      _emitOpenError('empty_response_body');
      return { html: fallbackHtml, fromUrl: false };
    } catch (e) {
      console.warn('[Ebook] Fetch error, using fallback product description HTML:', e);
      _emitOpenError('fetch_threw_' + (e && e.message ? e.message : 'unknown'));
      return { html: fallbackHtml, fromUrl: false };
    }
  }

  const sourceResult = await loadSourceHtml();
  const sourceHtml = sourceResult.html;
  const fromUrl = sourceResult.fromUrl;

  if (sourceHtml.trim()) {
    let html = sourceHtml;

    if (fromUrl) {
      // URL-fetched content is raw text — no entity decoding needed.
      // The textarea decode loop silently truncates strings >~10 MB in browsers,
      // so skipping it preserves the full file.
      console.log('[Ebook] Skipping entity decode (content from URL, ' + html.length + ' bytes)');
    } else {
      // Shopify product description needs HTML entity decoding (Liquid escapes the HTML).
      console.log('[Ebook] Running entity decode (content from product description)');
      const decoder = document.createElement('textarea');
      let prevHtml = '';
      let maxIterations = 5;

      while (html !== prevHtml && maxIterations > 0) {
        prevHtml = html;
        decoder.innerHTML = html;
        html = decoder.value;
        maxIterations--;
      }
    }

    // Split by H6 tags - Connor uses H6 for page numbers like "Page 1", "Page 2"
    const h6Pattern = /<h6[^>]*>[\s\S]*?<\/h6>/gi;
    const h6Matches = html.match(h6Pattern) || [];
    const sections = html.split(h6Pattern);

    console.log('[Ebook] Found ' + h6Matches.length + ' page markers (h6 tags)');

    // Process each H6 and its following content
    for (let i = 0; i < h6Matches.length; i++) {
      const pageNumberTag = h6Matches[i];
      const content = sections[i + 1] || '';

      // Extract page number text (e.g., "Page 1")
      const pageNumber = pageNumberTag.replace(/<[^>]*>/g, '').trim();

      // Strip embedded <script> blocks — the content-protection decoder is duplicated
      // on every page in Connor's file but only needs to live once in the theme.
      const cleanedContent = content.trim().replace(/<script[\s\S]*?<\/script>/gi, '');

      // Build page array: [pageNumber, htmlContent]
      pages.push([pageNumber, cleanedContent]);
    }

    console.log('[Ebook] Parsed ' + pages.length + ' pages');
  }

  // Fallback if no content parsed
  if (pages.length === 0) {
    pages = [
      [
        'No Content',
        '',
        'Please add content to the product description using H6 headings for page numbers.',
        '',
        'Example: <h6>Page 1</h6> followed by your content.',
      ],
    ];
  }

  console.log('[Ebook] Loaded ' + pages.length + ' pages');

  let currentPage = 0;
  const totalPages = pages.length;
  let isPlaying = false;
  let isAudioExpanded = false;
  let turnJsInitialized = false;

  // Navigation lock to prevent double page turns
  let isNavigating = false;
  const NAVIGATION_COOLDOWN = 600; // ms - must exceed Turn.js duration (500ms) to prevent race conditions

  // Flags to bypass the large-jump guard during programmatic navigation
  let isProgrammaticNavigation = false;
  let isRestoringProgress = false;
  let isOnCover = true;

  // ===== Analytics session state =====
  let pageEnterTime = Date.now();      // when the current page became visible (for page_dwell)
  let maxPageReached = 1;              // max page index reached this session (1-indexed)
  let pagesViewedInSession = 0;        // total navigations this session
  let sessionStartTime = Date.now();   // for reading_session_end duration
  let lastNavMethod = 'programmatic';  // set by each nav caller, read in saveReadingProgress
  let previousPageForDwell = 1;        // page the user is leaving (for page_dwell + direction)
  let lastSentChapterIndex = null;     // dedupe chapter_view events
  let sessionEndSent = false;          // ensure reading_session_end fires at most once
  const firedMilestones = new Set();   // dedupe milestone_reached (25/50/75/100)

  // Zoom recovery: known-good dimensions captured at scale=1
  var knownGoodDimensions = null;
  var wasZoomed = false;
  var activeJumpViewportResetCleanup = null;

  function getViewportScale() {
    return window.visualViewport && typeof window.visualViewport.scale === 'number'
      ? window.visualViewport.scale
      : 1;
  }

  function getFlipbookDimensions() {
    const readerContentEl = document.getElementById('reader-content');
    const desktop = isDesktopView();

    // A tablet in portrait is wide enough that the 0.75 page ratio makes the
    // page TALLER than the available height, so height caps it and the width
    // ends up well short of the screen (886 of 1024 on an iPad). 0.83 is the
    // ratio at which an iPad portrait page fills its width and still fits
    // vertically. Phones are under 768px and keep 0.75 exactly as before —
    // they are width-limited anyway, so the ratio barely affects them.
    const tablet = !desktop && window.innerWidth >= 768;
    const pageAspectRatio = desktop ? 0.65 : (tablet ? 0.83 : 0.75);
    const bookAspectRatio = desktop ? pageAspectRatio * 2 : pageAspectRatio;

    // Desktop grows with the viewport instead of freezing at 920px, which
    // left large monitors with a small book floating in dead space.
    // The non-desktop cap was a flat 760px. Phones are far narrower than that,
    // so the cap never bound for them and Math.max keeps their result
    // byte-identical; only a tablet in portrait is wide enough to reach it,
    // which is why an iPad could not fill its screen.
    const maxWidth = desktop
      ? Math.min(1600, Math.max(920, window.innerWidth - 80))
      : Math.min(1100, Math.max(760, window.innerWidth - 40));

    var footerEl = document.querySelector('.reader-footer');
    var footerHeight = footerEl ? footerEl.offsetHeight : 56;
    var dividerEl = document.querySelector('.reader-divider');
    var dividerHeight = dividerEl ? dividerEl.offsetHeight : 24;
    var safetyMargin = 16;
    var viewportHeight = window.innerHeight;
    var trueAvailableHeight;

    if (desktop && readerContentEl) {
      // Measure where the content box actually starts — this already accounts for
      // the real site header + nav bar instead of assuming a hardcoded 60 + 48.
      var contentTop = readerContentEl.getBoundingClientRect().top;
      trueAvailableHeight = viewportHeight - contentTop - footerHeight - dividerHeight - safetyMargin;
    } else {
      var headerHeight = desktop ? 60 : 44;
      var navBarEl = document.querySelector('.reader-nav-bar');
      var navBarHeight = navBarEl ? navBarEl.offsetHeight : (desktop ? 48 : 44);
      trueAvailableHeight = viewportHeight - (headerHeight + navBarHeight + footerHeight + dividerHeight + safetyMargin);
    }

    if (!readerContentEl) {
      var fallbackWidth = Math.min(maxWidth, window.innerWidth - 60);
      var fallbackHeight = Math.min(fallbackWidth / bookAspectRatio, trueAvailableHeight);
      return {
        width: Math.floor(fallbackWidth),
        height: Math.floor(fallbackHeight)
      };
    }

    var styles = window.getComputedStyle(readerContentEl);
    var horizontalPadding = (parseFloat(styles.paddingLeft) || 0) + (parseFloat(styles.paddingRight) || 0);
    var contentPaddingTop = parseFloat(styles.paddingTop) || 0;

    var availableWidth = Math.max(280, readerContentEl.clientWidth - horizontalPadding);
    var availableHeight = Math.max(320, trueAvailableHeight - contentPaddingTop);

    var width = Math.min(maxWidth, availableWidth);
    var height = width / bookAspectRatio;

    if (height > availableHeight) {
      height = availableHeight;
      width = height * bookAspectRatio;
    }

    return {
      width: Math.floor(width),
      height: Math.floor(height)
    };
  }

  function resetReaderScrollSurfaces(label) {
    var br = document.getElementById('book-reader');
    var pcs = document.querySelectorAll('#flipbook .page-content');
    var nonZero = [];
    if (window.scrollY) nonZero.push('window=' + window.scrollY);
    if (document.body && document.body.scrollTop) nonZero.push('body=' + document.body.scrollTop);
    if (document.documentElement && document.documentElement.scrollTop) {
      nonZero.push('docEl=' + document.documentElement.scrollTop);
    }
    if (br && br.scrollTop) nonZero.push('book-reader=' + br.scrollTop);
    for (var i = 0; i < pcs.length; i++) {
      if (pcs[i].scrollTop) nonZero.push('pc[' + i + ']=' + pcs[i].scrollTop);
    }
    if (nonZero.length) {
      console.log('[Ebook] ' + label + ' reset: ' + nonZero.join(' '));
    }
    try { window.scrollTo(0, 0); } catch (_) {}
    if (document.body) document.body.scrollTop = 0;
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (br) br.scrollTop = 0;
    for (var j = 0; j < pcs.length; j++) pcs[j].scrollTop = 0;
  }

  function scheduleReaderSurfaceReset(source, options) {
    options = options || {};
    var startDelay = typeof options.startDelay === 'number' ? options.startDelay : 0;
    var includeLatePass = !!options.includeLatePass;

    function runResetPasses() {
      resetReaderScrollSurfaces(source + ' immediate');
      requestAnimationFrame(function() {
        resetReaderScrollSurfaces(source + ' raf');
      });
      setTimeout(function() {
        resetReaderScrollSurfaces(source + ' 350ms');
      }, 350);
      if (includeLatePass) {
        setTimeout(function() {
          resetReaderScrollSurfaces(source + ' 700ms');
        }, 700);
      }
    }

    if (startDelay > 0) {
      setTimeout(runResetPasses, startDelay);
      return;
    }
    runResetPasses();
  }

  function armJumpViewportSettleReset(source) {
    if (activeJumpViewportResetCleanup) {
      activeJumpViewportResetCleanup();
      activeJumpViewportResetCleanup = null;
    }

    if (!window.visualViewport) {
      return;
    }

    var settled = false;
    var timeoutId = null;
    var cleanup = function() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      window.visualViewport.removeEventListener('resize', onResize);
      if (activeJumpViewportResetCleanup === cleanup) {
        activeJumpViewportResetCleanup = null;
      }
    };
    var onResize = function() {
      if (settled) return;
      settled = true;
      resetReaderScrollSurfaces(source + ' vp-resize');
      requestAnimationFrame(function() {
        resetReaderScrollSurfaces(source + ' vp-resize-raf');
      });
      cleanup();
    };

    activeJumpViewportResetCleanup = cleanup;
    window.visualViewport.addEventListener('resize', onResize, { once: false });
    timeoutId = setTimeout(function() {
      cleanup();
    }, 1200);
  }

  // ========== SECTION: Turn.js Integration (Build, Init, Resize) ==========
  /**
   * Build Turn.js pages dynamically from parsed content
   * Creates a div for each page with content and page number
   */
  function buildTurnJsPages() {
    if (typeof jQuery === 'undefined' || typeof $.fn.turn === 'undefined') {
      console.warn('Turn.js not loaded, using fallback mode');
      return false;
    }

    const flipbook = $('#flipbook');

    // Clear existing dynamic pages (keep covers)
    flipbook.find('.page:not(.hard)').remove();

    // Log pages being added to Turn.js
    console.log('🔧 Building Turn.js pages...');

    // Add each page as a div IN ORDER
    pages.forEach((pageData, index) => {
      const pageNumber = pageData[0];
      const content = pageData[1];

      const pageDiv = $('<div>').addClass('page').attr('data-page', index);
      const pageContent = $('<div>').addClass('page-content').html(content);
      const pageIndicator = $('<div>').addClass('page-number').text(pageNumber);

      pageDiv.append(pageContent).append(pageIndicator);
      flipbook.append(pageDiv);

      // Log first 3 and last 3 pages
      if (index < 3 || index >= pages.length - 3) {
        console.log(`  Added Turn.js page ${index + 1}: "${pageNumber}"`);
      }
    });

    console.log(`✅ Built ${pages.length} Turn.js pages`);
    return true;
  }

  /**
   * Initialize Turn.js flipbook
   */
  function initTurnJs() {
    if (typeof jQuery === 'undefined' || typeof $.fn.turn === 'undefined') {
      console.warn('Turn.js not loaded, skipping initialization');
      return false;
    }

    if (turnJsInitialized) {
      //       console.log('Turn.js already initialized');
      return true;
    }

    // Build pages first
    if (!buildTurnJsPages()) {
      return false;
    }

    const { width, height } = getFlipbookDimensions();

    // Responsive display mode: single on mobile, double on desktop
    const displayMode = isDesktopView() ? 'double' : 'single';

    // Initialize Turn.js
    $('#flipbook').turn({
      width: width,
      height: height,
      display: displayMode,
      autoCenter: true,
      duration: 500, // Faster page turn animation
      acceleration: true, // Enable hardware acceleration
      gradients: true,
      elevation: 50,
      page: 1, // Start at cover page
      when: {
        turning: function (e, page, view) {
          const oldCurrentPage = currentPage;

          if (page === 1) {
            isOnCover = true;
            updateDisplay();
            return;
          }

          isOnCover = false;
          const newCurrentPage = Math.max(0, Math.min(page - 2, totalPages - 1));

          if (!isRestoringProgress && !isProgrammaticNavigation) {
            const pageDiff = Math.abs(newCurrentPage - oldCurrentPage);
            if (pageDiff > totalPages / 2) {
              console.warn('Large jump blocked:', oldCurrentPage, '→', newCurrentPage);
              e.preventDefault();
              return false;
            }
          }

          currentPage = newCurrentPage;
          console.log('Page:', page, '→ currentPage:', currentPage, 'isOnCover:', isOnCover);
          updateDisplay();

          setTimeout(() => {
            saveReadingProgress();
            if (!isOnCover) syncAudioToVisibleChapter();
          }, 0);
        },

        turned: function (e, page) {
          updateFontSize();
          updatePageScrolling();
          resetReaderScrollSurfaces('Turned callback');

          // Sync counter to actual Turn.js state
          var actual = $('#flipbook').turn('page');
          if (actual === 1) { isOnCover = true; }
          else { isOnCover = false; currentPage = Math.max(0, Math.min(actual - 2, totalPages - 1)); }
          updateDisplay();

          // Only release locks if goToPage isn't managing them
          if (!isProgrammaticNavigation) {
            isNavigating = false;
          }

          // Force a dimension recheck after every turn to catch any corruption.
          // Only upsize, downsizes from post-Turn.js auto-margin shrinkage would
          // lock the flipbook at the small fallback. Real downsizes go through
          // the resize handler.
          requestAnimationFrame(function() {
            try {
              var scale = getViewportScale();
              if (scale > 1.05 || wasZoomed) return;
              var fresh = getFlipbookDimensions();
              var current = $('#flipbook').turn('size');
              if (current && fresh.width > current.width + 3) {
                $('#flipbook').turn('size', fresh.width, fresh.height);
              }
            } catch(e) {}
          });
        }
      }
    });

    turnJsInitialized = true;

    // Disable Turn.js internal click/tap handling to prevent double triggers
    // We handle all navigation through our own handlePageTap function
    $('#flipbook').off('click');
    $('#flipbook').off('mousedown');
    $('#flipbook').off('touchstart');

    console.log('✅ Turn.js initialized with fast navigation lock');

    // Capture initial dimensions as known-good at scale=1
    knownGoodDimensions = { width: width, height: height };

    function updateKnownGoodDimensions() {
      var scale = getViewportScale();
      if (Math.abs(scale - 1) < 0.05) {
        var fresh = getFlipbookDimensions();
        if (fresh.width >= 280 && fresh.height >= 320) {
          knownGoodDimensions = fresh;
        }
      }
    }

    // Handle responsive resizing (ignore pinch-zoom events)
    let resizeTimeout;
    let lastWindowWidth = window.innerWidth;
    let lastWindowHeight = window.innerHeight;
    let lastDisplayMode = displayMode;

    /**
     * Unconditional re-measure and re-apply. Unlike recheckDimensions (which
     * only ever grows the flipbook) this also SHRINKS it, and unlike the
     * debounced resize handler it ignores the zoom guards entirely.
     *
     * That combination is what rotation needs. iPadOS reports a transient
     * visualViewport scale above 1.05 while the device is turning, which makes
     * the resize handler set wasZoomed and skip — and the next resize then
     * takes the "post-zoom recovery" path, restoring knownGoodDimensions from
     * BEFORE the rotation. That is why landscape came back at the old size.
     */
    function forceResizeFlipbook(label) {
      try {
        if (!turnJsInitialized || typeof $.fn.turn === 'undefined') return;

        // Rotation is not a zoom — clear the flags so neither guard fires.
        wasZoomed = false;

        var fresh = getFlipbookDimensions();
        var mode = isDesktopView() ? 'double' : 'single';

        if (mode !== lastDisplayMode) {
          $('#flipbook').turn('display', mode);
          lastDisplayMode = mode;
        }
        $('#flipbook').turn('size', fresh.width, fresh.height);

        knownGoodDimensions = { width: fresh.width, height: fresh.height };
        lastWindowWidth = window.innerWidth;
        lastWindowHeight = window.innerHeight;

        updateFontSize();
        updateDisplay();
        console.log('[Ebook] ' + label + ': ' + mode + ' ' + fresh.width + 'x' + fresh.height);
      } catch (e) {}
    }
    $(window).on('resize', function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        var scale = getViewportScale();

        // Crossing the single/double breakpoint (rotation, or a window dragged
        // across 1024px) is always a real layout change. Handle it before the
        // zoom guards below, which would otherwise swallow it mid-rotation.
        var modeNow = isDesktopView() ? 'double' : 'single';
        if (modeNow !== lastDisplayMode) {
          forceResizeFlipbook('Display mode change');
          return;
        }

        // If page is zoomed in, mark it and skip resize
        if (scale > 1.05) {
          wasZoomed = true;
          console.log('[Ebook] Zoom detected, ignoring resize. Scale:', scale);
          return;
        }

        // If we just came back from a zoom, restore known-good dimensions
        // instead of measuring the possibly-corrupted layout
        if (wasZoomed) {
          wasZoomed = false;
          console.log('[Ebook] Post-zoom recovery, restoring known-good:', knownGoodDimensions.width + 'x' + knownGoodDimensions.height);
          $('#flipbook').turn('size', knownGoodDimensions.width, knownGoodDimensions.height);
          lastWindowWidth = window.innerWidth;
          lastWindowHeight = window.innerHeight;
          // Schedule a delayed re-measure once layout has fully settled
          setTimeout(function() {
            updateKnownGoodDimensions();
            recheckDimensions('Post-zoom settle');
          }, 500);
          return;
        }

        var currentWidth = window.innerWidth;
        var currentHeight = window.innerHeight;
        // Desktop also reacts to height-only resizes (dragging a window shorter
        // or taller). Mobile still ignores them so the iOS URL bar showing and
        // hiding doesn't retrigger a resize on every scroll.
        var heightChanged = isDesktopView() && Math.abs(currentHeight - lastWindowHeight) >= 5;
        if (Math.abs(currentWidth - lastWindowWidth) < 5 && !heightChanged) {
          return;
        }
        lastWindowWidth = currentWidth;
        lastWindowHeight = currentHeight;

        const fresh = getFlipbookDimensions();
        const newDisplayMode = isDesktopView() ? 'double' : 'single';
        $('#flipbook').turn('display', newDisplayMode);
        lastDisplayMode = newDisplayMode;
        $('#flipbook').turn('size', fresh.width, fresh.height);
        knownGoodDimensions = { width: fresh.width, height: fresh.height };
        updateFontSize();
      }, 250);
    });

    // Staggered recalculations to catch late layout shifts on cold first visit.
    // Only upsize to avoid locking in a shrunken layout.
    function recheckDimensions(label) {
      try {
        var fresh = getFlipbookDimensions();
        var current = $('#flipbook').turn('size');
        if (!current) return;
        if (fresh.width > current.width + 3) {
          console.log('[Ebook] ' + label + ':', current.width + 'x' + current.height, '->', fresh.width + 'x' + fresh.height);
          $('#flipbook').turn('size', fresh.width, fresh.height);
          updateKnownGoodDimensions();
        }
      } catch(e) {}
    }

    // ===== Rotation handling =====
    // Staggered because iOS reports the new innerWidth/innerHeight before the
    // layout has actually settled — a single immediate measure locks in the
    // half-rotated size. Re-running unconditionally means the last pass always
    // wins with correct numbers.
    function handleOrientationChange(source) {
      [100, 350, 700, 1200].forEach(function (t) {
        setTimeout(function () {
          forceResizeFlipbook(source + ' +' + t + 'ms');
        }, t);
      });
    }

    window.addEventListener('orientationchange', function () {
      handleOrientationChange('Orientation');
    });

    // matchMedia is the modern signal and fires on iPadOS where
    // orientationchange is deprecated; both are harmless if both fire.
    if (window.matchMedia) {
      var portraitQuery = window.matchMedia('(orientation: portrait)');
      var onOrientationQuery = function () { handleOrientationChange('Orientation query'); };
      if (portraitQuery.addEventListener) {
        portraitQuery.addEventListener('change', onOrientationQuery);
      } else if (portraitQuery.addListener) {
        portraitQuery.addListener(onOrientationQuery);
      }
    }

    setTimeout(function() { recheckDimensions('Recheck 300ms'); }, 300);
    setTimeout(function() { recheckDimensions('Recheck 800ms'); }, 800);
    setTimeout(function() { recheckDimensions('Recheck 1500ms'); }, 1500);
    setTimeout(function() { recheckDimensions('Recheck 3000ms'); }, 3000);

    // Also recalculate when fonts finish loading
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function() {
        requestAnimationFrame(function() {
          recheckDimensions('Font-load');
        });
      });
    }

    // Listen for iOS Safari viewport changes (URL bar show/hide, zoom recovery)
    if (window.visualViewport) {
      var lastVpScale = 1;
      window.visualViewport.addEventListener('resize', function() {
        var scale = getViewportScale();

        // User is actively zooming, track it
        if (scale > 1.05) {
          wasZoomed = true;
          lastVpScale = scale;
          return;
        }

        // Just returned to scale ~1.0 from a zoom: restore known-good
        if (lastVpScale > 1.05 && Math.abs(scale - 1) < 0.05) {
          lastVpScale = scale;
          wasZoomed = false;
          console.log('[Ebook] VisualViewport zoom-out recovery, restoring:', knownGoodDimensions.width + 'x' + knownGoodDimensions.height);
          $('#flipbook').turn('size', knownGoodDimensions.width, knownGoodDimensions.height);
          // Delayed settle recheck
          setTimeout(function() {
            updateKnownGoodDimensions();
            recheckDimensions('VP-zoom-settle');
          }, 500);
          return;
        }

        lastVpScale = scale;
        recheckDimensions('VisualViewport');
      });
    }

    return true;
  }

  // ========== SECTION: DOM Elements & State ==========
  const pageContent = document.getElementById('page-text');
  const pageChapterTitle = document.getElementById('page-chapter-title');
  const pageCounter = document.getElementById('page-counter');
  const footerPageCount = document.getElementById('footer-page-count');
  const pageNumberBottom = document.getElementById('page-number-bottom');
  const prevBtn = document.getElementById('prev-btn');
  const nextBtn = document.getElementById('next-btn');
  const chapterMenu = document.getElementById('chapter-menu');
  const chapterBtn = document.getElementById('chapter-btn');
  const currentChapterName = document.getElementById('current-chapter-name');
  const pageMenu = document.getElementById('page-menu');
  const pageBtn = document.getElementById('page-btn');

  // Bookmark elements
  const bookmarkBtn = document.getElementById('bookmark-btn');
  const bookmarkIcon = document.getElementById('bookmark-icon');
  const bookmarksBtn = document.getElementById('bookmarks-btn');
  const bookmarksMenu = document.getElementById('bookmarks-menu');
  const bookmarksList = document.getElementById('bookmarks-list');
  const bookmarksCount = document.getElementById('bookmarks-count');
  const bookmarksEmpty = document.getElementById('bookmarks-empty');

  // Get book ID for localStorage key
  const bookId = window.location.pathname.split('/').pop() || 'default-book';
  const bookmarksKey = `wishful_bookmarks_${bookId}`;

  // Track if book_started event has been sent this session
  let bookStartedSent = false;
  let bookCompletedSent = false;

  // Get book title helper
  function getBookTitle() {
    return document.querySelector('.book-title')?.textContent ||
      document.querySelector('h1')?.textContent ||
      bookId;
  }

  // ========== SECTION: Analytics (GA4) ==========
  // Shared context spread into every reader-side event so taxonomy stays uniform.
  function getBookContext() {
    return {
      book_id: bookId,
      book_title: getBookTitle(),
      mode: isReadOnly ? 'read-only' : 'read-listen'
    };
  }

  // Taxonomy reference — every event we send and its parameter keys, in one place.
  // Update this when adding/removing events so the contract stays visible.
  const ANALYTICS_EVENTS = {
    mode_selected:           ['book_id','book_title','mode','has_audio'],
    cta_start_reading_click: ['source_page','book_id','mode'],
    book_started:            ['book_id','book_title','mode','total_pages'],
    book_page_view:          ['book_id','book_title','mode','page_number','total_pages','percentage','is_resume'],
    book_completed:          ['book_id','book_title','mode','total_pages','percentage'],
    page_turn:               ['book_id','book_title','mode','from_page','to_page','direction','method'],
    page_dwell:              ['book_id','book_title','mode','page_number','dwell_seconds','direction','total_pages'],
    milestone_reached:       ['book_id','book_title','mode','milestone','page_number'],
    chapter_view:            ['book_id','book_title','mode','chapter_name','chapter_index','page_number'],
    reading_resumed:         ['book_id','book_title','mode','resumed_page','percentage'],
    reading_session_end:     ['book_id','book_title','mode','last_page','max_page_reached','percentage','pages_viewed_in_session','session_duration_seconds'],
    book_open_error:         ['book_id','book_title','mode','error_message'],
    asset_load_error:        ['book_id','book_title','mode','asset_url','page_number'],
    audio_play:              ['book_id','book_title','mode','page_number','audio_position_seconds'],
    audio_pause:             ['book_id','book_title','mode','page_number','audio_position_seconds'],
    audio_complete:          ['book_id','book_title','mode','page_number','audio_position_seconds']
  };

  function sendGA4Event(eventName, params) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, params);
      //       console.log('[GA4] Sent', eventName, 'event', params);
    }
  }

  // ========== SECTION: Progress Persistence (Cookie + localStorage) ==========
  function saveReadingProgress() {
    try {
      // Get existing progress from cookie
      let progress = {};
      const cookie = document.cookie.split('; ').find(row => row.startsWith('book_progress='));
      if (cookie) {
        progress = JSON.parse(decodeURIComponent(cookie.split('=')[1]));
      }

      // Calculate percentage (1-indexed for display)
      const pageNum = currentPage + 1;
      const percentage = Math.round((pageNum / totalPages) * 100);

      // Update progress for this book with enhanced data
      progress[bookId] = {
        page: pageNum,
        total: totalPages,
        pct: percentage
      };

      //       console.log('[Progress] Saving progress for book:', bookId);
      //       console.log('[Progress] Page:', pageNum, '/', totalPages, '=', percentage + '%');
      //       console.log('[Progress] Full cookie data:', progress);

      // Save to cookie (expires in 1 year)
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = 'book_progress=' + encodeURIComponent(JSON.stringify(progress)) + '; expires=' + expires.toUTCString() + '; path=/';

      //       console.log('[Progress] Cookie saved successfully!');

      // ===== ENHANCED PROGRESS PERSISTENCE =====
      // Save detailed progress to localStorage including chapter and audio position
      try {
        const currentChapter = getCurrentChapter();
        const chapterIndex = currentChapter ? manualChapters.findIndex(ch => ch.name === currentChapter.name) : -1;

        const detailedProgress = {
          bookId: bookId,
          page: currentPage, // 0-indexed for internal use
          chapterIndex: chapterIndex,
          audioTime: audioElement && !isNaN(audioElement.currentTime) ? audioElement.currentTime : 0,
          lastUpdated: Date.now()
        };

        localStorage.setItem(`wishful_detailed_progress_${bookId}`, JSON.stringify(detailedProgress));
        //         console.log('[Progress] Detailed progress saved to localStorage:', detailedProgress);
      } catch (e) {
        console.warn('[Progress] Could not save detailed progress to localStorage:', e);
      }

      // ===== GA4 events =====
      const ctx = getBookContext();

      // page_turn + page_dwell: only fire on a REAL page change (not a same-page save).
      // pageNum is 1-indexed; previousPageForDwell tracks the page being left.
      if (pageNum !== previousPageForDwell) {
        const direction = pageNum > previousPageForDwell ? 'forward' : 'backward';
        const dwellSeconds = Math.max(0, Math.round((Date.now() - pageEnterTime) / 1000));

        sendGA4Event('page_dwell', Object.assign({}, ctx, {
          page_number: previousPageForDwell,
          dwell_seconds: dwellSeconds,
          direction: direction,
          total_pages: totalPages
        }));

        sendGA4Event('page_turn', Object.assign({}, ctx, {
          from_page: previousPageForDwell,
          to_page: pageNum,
          direction: direction,
          method: lastNavMethod
        }));

        // Reset per-page session counters
        pageEnterTime = Date.now();
        previousPageForDwell = pageNum;
        lastNavMethod = 'programmatic'; // consumed; default to programmatic until next caller sets it
      }
      pagesViewedInSession += 1;
      if (pageNum > maxPageReached) maxPageReached = pageNum;

      // Send book_started event (only once per session)
      if (!bookStartedSent) {
        sendGA4Event('book_started', Object.assign({}, ctx, {
          total_pages: totalPages
        }));
        bookStartedSent = true;
      }

      // Send book_page_view event (every page turn).
      // is_resume tags the view that fires during restoreProgress so it stays
      // filterable without changing behaviour.
      const pageViewParams = Object.assign({}, ctx, {
        page_number: pageNum,
        total_pages: totalPages,
        percentage: percentage
      });
      if (isRestoringProgress) pageViewParams.is_resume = true;
      sendGA4Event('book_page_view', pageViewParams);

      // chapter_view: fire when the current chapter index changes.
      try {
        const currentChapterForEvent = getCurrentChapter();
        if (currentChapterForEvent) {
          const chapterIndexForEvent = manualChapters.findIndex(ch => ch.name === currentChapterForEvent.name);
          if (chapterIndexForEvent !== -1 && chapterIndexForEvent !== lastSentChapterIndex) {
            sendGA4Event('chapter_view', Object.assign({}, ctx, {
              chapter_name: currentChapterForEvent.name,
              chapter_index: chapterIndexForEvent,
              page_number: pageNum
            }));
            lastSentChapterIndex = chapterIndexForEvent;
          }
        }
      } catch (_) { /* chapter lookup failed — non-fatal */ }

      // milestone_reached: 25/50/75/100. Each fires at most once per session.
      [25, 50, 75, 100].forEach(function (m) {
        if (!firedMilestones.has(m) && percentage >= m) {
          firedMilestones.add(m);
          sendGA4Event('milestone_reached', Object.assign({}, ctx, {
            milestone: m,
            page_number: pageNum
          }));
        }
      });

      // Send book_completed event (only once when reaching 100%)
      if (percentage === 100 && !bookCompletedSent) {
        sendGA4Event('book_completed', Object.assign({}, ctx, {
          total_pages: totalPages,
          percentage: percentage
        }));
        bookCompletedSent = true;
      }
    } catch (e) {
      //       console.log('[Progress] ERROR - Could not save reading progress:', e);
    }
  }

  // Load bookmarks from localStorage
  let bookmarks = [];
  try {
    const saved = localStorage.getItem(bookmarksKey);
    if (saved) {
      bookmarks = JSON.parse(saved);
    }
  } catch (e) {
    //     console.log('Could not load bookmarks');
  }

  // ========== SECTION: Bookmarks ==========
  function saveBookmarks() {
    try {
      localStorage.setItem(bookmarksKey, JSON.stringify(bookmarks));
    } catch (e) {
      //       console.log('Could not save bookmarks');
    }
  }

  // Check if current page is bookmarked
  function isPageBookmarked(pageIndex) {
    return bookmarks.some((b) => b.page === pageIndex);
  }

  // Toggle bookmark for current page
  function toggleBookmark() {
    const pageIndex = currentPage;
    const existingIndex = bookmarks.findIndex((b) => b.page === pageIndex);

    if (existingIndex >= 0) {
      // Remove bookmark
      bookmarks.splice(existingIndex, 1);
    } else {
      // Add bookmark
      const pageTitle = pages[pageIndex]?.[0] || `Page ${pageIndex + 1}`;
      const chapter = getCurrentChapter();
      bookmarks.push({
        page: pageIndex,
        title: pageTitle,
        chapter: chapter ? chapter.name : pageTitle,
        timestamp: Date.now(),
      });
      // Sort by page number
      bookmarks.sort((a, b) => a.page - b.page);
    }

    saveBookmarks();
    updateBookmarkUI();
  }

  // Update bookmark button and list UI
  function updateBookmarkUI() {
    // Update bookmark button state
    if (bookmarkBtn) {
      if (isPageBookmarked(currentPage)) {
        bookmarkBtn.classList.add('active');
      } else {
        bookmarkBtn.classList.remove('active');
      }
    }

    // Update count
    if (bookmarksCount) {
      bookmarksCount.textContent = bookmarks.length;
    }

    // Update bookmarks list
    if (bookmarksList) {
      bookmarksList.innerHTML = '';
      bookmarks.forEach((bookmark, index) => {
        const div = document.createElement('div');
        div.className = 'bookmark-item';
        div.innerHTML = `
            <div class="bookmark-item-info">
              <div class="bookmark-item-page">${bookmark.title}</div>
              <div class="bookmark-item-chapter">${bookmark.chapter}</div>
            </div>
            <button class="bookmark-item-delete" data-index="${index}" title="Remove bookmark">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          `;

        // Click to go to page
        div.querySelector('.bookmark-item-info').onclick = () => {
          goToPage(bookmark.page);
          if (bookmarksMenu) bookmarksMenu.classList.remove('active');
        };

        // Delete button
        div.querySelector('.bookmark-item-delete').onclick = (e) => {
          e.stopPropagation();
          bookmarks.splice(index, 1);
          saveBookmarks();
          updateBookmarkUI();
        };

        bookmarksList.appendChild(div);
      });
    }

    // Show/hide empty message
    if (bookmarksEmpty) {
      bookmarksEmpty.style.display = bookmarks.length === 0 ? 'block' : 'none';
    }
  }

  // Bookmark button click
  if (bookmarkBtn) {
    bookmarkBtn.onclick = toggleBookmark;
  }

  // Bookmarks dropdown toggle
  if (bookmarksBtn && bookmarksMenu) {
    bookmarksBtn.onclick = (e) => {
      e.stopPropagation();
      bookmarksMenu.classList.toggle('active');
      // Close other menus
      if (chapterMenu) chapterMenu.classList.remove('active');
      if (pageMenu) pageMenu.classList.remove('active');
    };
  }

  // Audio elements
  const vinylMini = document.getElementById('vinyl-mini');
  const vinylLarge = document.getElementById('vinyl-large');
  const playBtnMini = document.getElementById('play-btn-mini');
  const playBtnLarge = document.getElementById('play-btn-large');
  const expandAudioBtn = document.getElementById('expand-audio-btn');
  const collapseAudioBtn = document.getElementById('collapse-audio-btn');
  const audioExpanded = document.getElementById('audio-expanded');
  const audioChapterTitle = document.getElementById('audio-chapter-title');
  const audioPageInfo = document.getElementById('audio-page-info');

  // Load manual chapters from Shopify admin
  let manualChapters = [];
  try {
    const chaptersDataEl = document.getElementById('manual-chapters-data');
    if (chaptersDataEl) {
      const rawChapters = JSON.parse(chaptersDataEl.textContent);
      // Parse timestamps and enhance chapter objects
      manualChapters = rawChapters.map(chapter => ({
        name: chapter.name,
        page: chapter.page,
        timestamp: parseTimestamp(chapter.timestamp || '0:00'),
        timestampStr: chapter.timestamp || '0:00'
      }));
      //       console.log('Manual chapters loaded with timestamps:', manualChapters);

      // ===== TIMESTAMP VALIDATION =====
      // Check if any chapters have valid timestamps
      const hasValidTimestamps = manualChapters.some(ch => ch.timestamp > 0);
      if (!hasValidTimestamps && !isReadOnly) {
        console.warn('[Timestamps] No valid timestamps found. Audio sync will not be available.');
        console.warn('[Timestamps] Text-only chapter navigation will still work.');
      }

      // Check for timestamp ordering issues
      for (let i = 1; i < manualChapters.length; i++) {
        if (manualChapters[i].timestamp < manualChapters[i - 1].timestamp && manualChapters[i].timestamp > 0) {
          console.warn('[Timestamps] Chapter timestamps are not in ascending order:',
            manualChapters[i - 1].name, '(' + manualChapters[i - 1].timestamp + 's) →',
            manualChapters[i].name, '(' + manualChapters[i].timestamp + 's)');
        }
      }
    }
  } catch (e) {
    //     console.log('No manual chapters defined or error parsing:', e);
  }

  // Auto-detect chapters from H2 tags if no manual chapters defined
  if (manualChapters.length === 0) {
    manualChapters = buildAutoChapters();
    if (manualChapters.length > 0) {
      console.log('[Chapters] Auto-detected', manualChapters.length, 'chapters from H2 tags:', manualChapters.map(c => c.name));
    }
  }

  // ========== SECTION: Chapter System (Detection, Navigation, Audio Sync) ==========
  function getCurrentChapter() {
    if (manualChapters.length === 0) return null;

    // Get the page number from current page content (e.g., "Page 15")
    const pageText = pages[currentPage]?.[0] || '';
    const pageMatch = pageText.match(/Page\s*(\d+)/i);
    const currentPageNum = pageMatch ? parseInt(pageMatch[1]) : currentPage + 1;

    // Find the chapter that contains this page
    let currentChapter = manualChapters[0];
    for (const chapter of manualChapters) {
      if (chapter.page <= currentPageNum) {
        currentChapter = chapter;
      } else {
        break;
      }
    }
    return currentChapter;
  }

  /**
   * Like getCurrentChapter, but accounts for BOTH pages of the desktop
   * two-page spread. getCurrentChapter only reads the left page, so when a
   * chapter starts on the right-hand page its heading is visible on screen
   * while the dropdown still highlights the previous chapter for that whole
   * spread. Used for the highlight and the nav-bar label only — audio sync
   * and progress saving deliberately still use getCurrentChapter, so audio
   * timing is unchanged.
   */
  function getVisibleChapter() {
    if (manualChapters.length === 0) return null;

    function pageNumberAt(index) {
      const text = pages[index]?.[0] || '';
      const match = text.match(/Page\s*(\d+)/i);
      return match ? parseInt(match[1]) : index + 1;
    }

    var visiblePageNum = pageNumberAt(currentPage);
    if (isDesktopView() && currentPage + 1 < totalPages) {
      visiblePageNum = Math.max(visiblePageNum, pageNumberAt(currentPage + 1));
    }

    let visibleChapter = manualChapters[0];
    for (const chapter of manualChapters) {
      if (chapter.page <= visiblePageNum) {
        visibleChapter = chapter;
      } else {
        break;
      }
    }
    return visibleChapter;
  }
  // Auto-detect chapters from H2 tags when no manual chapters defined
  function buildAutoChapters() {
    const autoChapters = [];
    for (let i = 0; i < pages.length; i++) {
      const htmlContent = pages[i][1] || '';
      const pageText = pages[i][0] || '';
      const pageMatch = pageText.match(/Page\s*(\d+)/i);
      const pageNum = pageMatch ? parseInt(pageMatch[1]) : i + 1;

      const h2Pattern = /<h2[^>]*>(.*?)<\/h2>/gi;
      let match;
      while ((match = h2Pattern.exec(htmlContent)) !== null) {
        const name = match[1].replace(/<[^>]*>/g, '').trim();
        if (name) {
          autoChapters.push({ name: name, page: pageNum, timestamp: 0, timestampStr: '0:00' });
        }
      }
    }
    return autoChapters;
  }

  // --- Chapter Navigation with Audio Sync ---
  /**
   * Seek to chapter - syncs both text and audio
   * @param {number} chapterIndex - Index in manualChapters array
   */
  function seekToChapter(chapterIndex) {
    if (chapterIndex < 0 || chapterIndex >= manualChapters.length) {
      console.warn('Invalid chapter index:', chapterIndex);
      return;
    }

    const chapter = manualChapters[chapterIndex];
    //     console.log('Seeking to chapter:', chapter);

    // Set programmatic flag before goToPage to ensure the large-jump guard allows it
    isProgrammaticNavigation = true;

    // Close menus before navigation
    if (chapterMenu) { chapterMenu.classList.remove('active'); chapterMenu.style.display = 'none'; }
    if (pageMenu) { pageMenu.classList.remove('active'); pageMenu.style.display = 'none'; }

    // Find the page index that matches this chapter's page number
    const targetPageIndex = pages.findIndex((page) => {
      const pageText = page[0] || '';
      const pageMatch = pageText.match(/Page\s*(\d+)/i);
      return pageMatch && parseInt(pageMatch[1]) === chapter.page;
    });

    // Scroll text to chapter's page
    if (targetPageIndex !== -1) {
      goToPage(targetPageIndex);
    } else {
      // Fallback: go to page index based on chapter page number
      goToPage(Math.min(chapter.page - 1, totalPages - 1));
    }

    // Seek audio to chapter's timestamp if in read-listen mode and audio available
    if (!isReadOnly && audioElement && chapter.timestamp >= 0) {
      try {
        audioElement.currentTime = chapter.timestamp;
        //         console.log('Audio seeked to:', chapter.timestamp, 'seconds');
      } catch (e) {
        console.warn('Could not seek audio:', e);
      }
    }
  }

  /**
   * Get current chapter based on audio position
   * @param {number} currentTime - Audio currentTime in seconds
   * @returns {object|null} - Chapter object or null
   */
  function getChapterAtTime(currentTime) {
    if (manualChapters.length === 0) {
      return null;
    }

    // Find the chapter that contains this timestamp
    // The chapter is the one whose timestamp is <= currentTime
    // and the next chapter's timestamp is > currentTime (or it's the last chapter)
    let currentChapter = manualChapters[0];

    for (let i = 0; i < manualChapters.length; i++) {
      const chapter = manualChapters[i];
      const nextChapter = manualChapters[i + 1];

      if (chapter.timestamp <= currentTime) {
        if (!nextChapter || nextChapter.timestamp > currentTime) {
          currentChapter = chapter;
          break;
        }
        currentChapter = chapter;
      }
    }

    return currentChapter;
  }

  // Track last synced chapter to avoid unnecessary seeks
  let lastSyncedChapter = null;

  /**
   * Sync audio to match visible chapter (text to audio sync)
   * Called when user manually navigates text
   */
  function syncAudioToVisibleChapter() {
    // Skip sync if user recently dragged progress bar (within block duration)
    const timeSinceLastSeek = Date.now() - lastUserSeekTime;
    if (timeSinceLastSeek < SEEK_SYNC_BLOCK_DURATION) {
      console.log(`DEBUG syncAudioToVisibleChapter: blocked, ${timeSinceLastSeek}ms since last seek`);
      return;
    }
    // Only sync in read-listen mode with audio available
    if (isReadOnly || !audioElement || manualChapters.length === 0) {
      return;
    }

    // Get current chapter based on visible page
    const visibleChapter = getCurrentChapter();

    if (!visibleChapter) {
      return;
    }

    // Only sync if chapter has changed to avoid unnecessary seeks
    if (visibleChapter === lastSyncedChapter) {
      return;
    }

    lastSyncedChapter = visibleChapter;

    // Seek audio to chapter's timestamp
    if (visibleChapter.timestamp >= 0) {
      try {
        audioElement.currentTime = visibleChapter.timestamp;
        //         console.log('Synced audio to visible chapter:', visibleChapter.name, 'at', visibleChapter.timestamp, 'seconds');
      } catch (e) {
        console.warn('Could not sync audio to visible chapter:', e);
      }
    }
  }

  // Generate chapters dropdown - uses manual chapters if defined
  function generateChapters() {
    //     console.log('Generating chapters, manualChapters:', manualChapters);
    chapterMenu.innerHTML = '';

    if (manualChapters.length > 0) {
      // Use manual chapters from Shopify admin
      manualChapters.forEach((chapter, index) => {
        const div = document.createElement('div');

        // Check if this is the current chapter. getVisibleChapter accounts
        // for both pages of the desktop spread, so a chapter starting on the
        // right-hand page highlights as soon as it is on screen.
        const currentChapter = getVisibleChapter();
        const isActive = currentChapter && currentChapter.name === chapter.name;

        div.className = 'chapter-item' + (isActive ? ' active' : '');

        // Display timestamp if available (not 0:00)
        const timestampDisplay = chapter.timestampStr && chapter.timestampStr !== '0:00'
          ? ` <span style="color: #999; font-size: 0.9em;">(${chapter.timestampStr})</span>`
          : '';

        div.innerHTML = `
            <div class="chapter-item-title">${chapter.name}${timestampDisplay}</div>
            <div class="chapter-item-pages">Page ${chapter.page}</div>
          `;

        div.onclick = () => {
          // Close the chapter menu first (both class and style)
          chapterMenu.classList.remove('active');
          chapterMenu.style.display = 'none';
          seekToChapter(index);
        };

        chapterMenu.appendChild(div);
      });

      // Update current chapter name display
      const currentChapter = getVisibleChapter();
      if (currentChapter && currentChapterName) {
        const name = currentChapter.name;
        currentChapterName.textContent = name.length > 12 ? name.substring(0, 12) + '...' : name;
      }
    } else {
      // Fallback: use page numbers as chapters
      pages.forEach((page, index) => {
        const title = page[0] || `Page ${index + 1}`;
        const div = document.createElement('div');
        div.className = 'chapter-item' + (index === currentPage ? ' active' : '');
        div.innerHTML = `
            <div class="chapter-item-title">${title.length > 25 ? title.substring(0, 25) + '...' : title}</div>
            <div class="chapter-item-pages">1 pages</div>
          `;
        div.onclick = () => {
          // Close the chapter menu first (both class and style)
          chapterMenu.classList.remove('active');
          chapterMenu.style.display = 'none';
          isProgrammaticNavigation = true;
          goToPage(index);
        };
        chapterMenu.appendChild(div);
      });
    }
  }

  // Generate smart page navigation (prev 5, input, next 5)
  function generateQuickPages() {
    const prevPagesList = document.getElementById('prev-pages-list');
    const nextPagesList = document.getElementById('next-pages-list');
    const prevSection = document.getElementById('prev-pages-section');
    const nextSection = document.getElementById('next-pages-section');
    const pageJumpTotal = document.getElementById('page-jump-total');
    const pageJumpInput = document.getElementById('page-jump-input');
    const pageJumpBtn = document.getElementById('page-jump-btn');

    // Update total pages display
    if (pageJumpTotal) pageJumpTotal.textContent = `/ ${totalPages}`;
    if (pageJumpInput) pageJumpInput.max = totalPages;

    // Generate previous 5 pages
    if (prevPagesList) {
      prevPagesList.innerHTML = '';
      const startPrev = Math.max(0, currentPage - 5);

      if (currentPage === 0) {
        // Hide prev section if on first page
        if (prevSection) prevSection.style.display = 'none';
      } else {
        if (prevSection) prevSection.style.display = 'block';
        for (let i = startPrev; i < currentPage; i++) {
          const div = document.createElement('div');
          div.className = 'page-item-mini';
          div.textContent = i + 1;
          div.onclick = () => {
            goToPage(i);
            pageMenu.classList.remove('active');
            pageMenu.style.display = 'none';
          };
          prevPagesList.appendChild(div);
        }
      }
    }

    // Generate next 5 pages
    if (nextPagesList) {
      nextPagesList.innerHTML = '';
      const endNext = Math.min(totalPages, currentPage + 6);

      if (currentPage >= totalPages - 1) {
        // Hide next section if on last page
        if (nextSection) nextSection.style.display = 'none';
      } else {
        if (nextSection) nextSection.style.display = 'block';
        for (let i = currentPage + 1; i < endNext; i++) {
          const div = document.createElement('div');
          div.className = 'page-item-mini';
          div.textContent = i + 1;
          div.onclick = () => {
            goToPage(i);
            pageMenu.classList.remove('active');
            pageMenu.style.display = 'none';
          };
          nextPagesList.appendChild(div);
        }
      }
    }

    // Page jump button handler
    if (pageJumpBtn && pageJumpInput) {
      // Error message element (created once, reused)
      var jumpError = document.createElement('div');
      jumpError.className = 'page-jump-error';
      pageJumpInput.closest('.page-jump-section').appendChild(jumpError);
      var jumpErrorTimeout;

      function showJumpError(msg) {
        clearTimeout(jumpErrorTimeout);
        jumpError.textContent = msg;
        jumpError.style.display = 'block';
        pageJumpInput.style.borderColor = '#c0392b';
        jumpErrorTimeout = setTimeout(function() {
          jumpError.style.display = 'none';
          pageJumpInput.style.borderColor = '';
        }, 3000);
      }

      pageJumpBtn.onclick = () => {
        var raw = pageJumpInput.value.trim();
        if (!raw) return;
        const targetPage = parseInt(raw, 10);
        if (isNaN(targetPage) || targetPage < 1) {
          showJumpError('Enter a page number from 1 to ' + totalPages);
          return;
        }
        if (targetPage > totalPages) {
          showJumpError('Last page is ' + totalPages);
          return;
        }
        jumpError.style.display = 'none';
        pageJumpInput.style.borderColor = '';

        pageJumpInput.blur();
        pageJumpInput.value = '';
        pageMenu.classList.remove('active');
        pageMenu.style.display = 'none';

        // Diagnostic + reset: log anything that's scrolled and clear it across
        // every container that iOS could have shifted when the keyboard opened.
        function jumpResetScroll(label) {
          resetReaderScrollSurfaces('Jump→' + targetPage + ' ' + label);
        }

        jumpResetScroll('pre');
        goToPage(targetPage - 1);
        scheduleReaderSurfaceReset('Jump→' + targetPage + ' settle', {
          startDelay: NAVIGATION_COOLDOWN,
          includeLatePass: true
        });
        armJumpViewportSettleReset('Jump→' + targetPage);
        // Re-reset after Turn.js settles in case iOS re-pans or layout shifts again.
        setTimeout(function() { jumpResetScroll('post'); }, NAVIGATION_COOLDOWN + 100);
      };

      // Also allow Enter key
      pageJumpInput.onkeydown = (e) => {
        if (e.key === 'Enter') {
          pageJumpBtn.click();
        }
      };
    }
  }

  // ========== SECTION: UI Updates & Display ==========
  function isDesktopView() {
    return window.innerWidth >= 1024;
  }

  // Update display
  function updateDisplay() {
    if (isOnCover) {
      if (pageCounter) pageCounter.textContent = 'Cover';
      if (footerPageCount) footerPageCount.textContent = 'Cover';
      if (prevBtn) { prevBtn.disabled = true; prevBtn.classList.add('disabled'); }
      if (nextBtn) { nextBtn.disabled = false; nextBtn.classList.remove('disabled'); }
      return;
    }

    const content = pages[currentPage];
    const title = content[0] || `Page ${currentPage + 1}`;

    // Get current chapter name from manual chapters. getVisibleChapter so the
    // toolbar label matches what the reader can actually see in the spread.
    const currentChapter = getVisibleChapter();
    const chapterLabel = currentChapter ? currentChapter.name : title;

    // Update page header - chapter on left, page number on right
    if (pageChapterTitle) pageChapterTitle.textContent = chapterLabel;
    const pageNumberRight = document.getElementById('page-number-right');
    if (pageNumberRight) {
      pageNumberRight.textContent = title;
    }

    // Check if desktop two-page spread mode
    if (isDesktopView()) {
      updateTwoPageSpread();
    } else {
      // Mobile: single page view
      const htmlContent = content[1] || '';
      if (pageContent) pageContent.innerHTML = htmlContent;
    }

    // Update counters - currentPage 0 = first content page (pages[0])
    if (isDesktopView()) {
      // Ask Turn.js which pages are ACTUALLY on screen rather than assuming
      // currentPage is the left one. Turn.js puts odd pages on the right, so
      // after jumping to an odd page currentPage is the right-hand page and
      // the old `currentPage + 1` / `currentPage + 2` reported this page plus
      // the NEXT one instead of this page and the one before it — the counter
      // read 124-125 while the pages themselves printed 123 and 124.
      var visibleIndexes = [];
      try {
        var view = $('#flipbook').turn('view');
        if (view && view.length) {
          for (var vi = 0; vi < view.length; vi++) {
            var turnPage = view[vi];
            // view can contain 0 for a blank side; page 1 is the cover
            // Turn.js page 1 is the cover, so content starts at page 2 and
            // index = turnPage - 2 (same mapping goToPage uses).
            if (turnPage >= 2) {
              var idx = turnPage - 2;
              if (idx >= 0 && idx < totalPages) visibleIndexes.push(idx);
            }
          }
        }
      } catch (e) {}

      if (!visibleIndexes.length) visibleIndexes = [currentPage];
      visibleIndexes.sort(function (a, b) { return a - b; });

      const leftPage = visibleIndexes[0] + 1;
      const rightPage = visibleIndexes[visibleIndexes.length - 1] + 1;
      const spreadText = leftPage === rightPage ? `${leftPage}/${totalPages}` : `${leftPage}-${rightPage}/${totalPages}`;
      if (pageCounter) pageCounter.textContent = spreadText;
      if (footerPageCount) footerPageCount.textContent = spreadText;
    } else {
      const counterText = `${currentPage + 1}/${totalPages}`;
      if (pageCounter) pageCounter.textContent = counterText;
      if (footerPageCount) footerPageCount.textContent = counterText;
    }

    if (pageNumberBottom) pageNumberBottom.textContent = `${title} of ${totalPages} pages`;
    if (currentChapterName)
      currentChapterName.textContent =
        chapterLabel.length > 12 ? chapterLabel.substring(0, 12) + '...' : chapterLabel;

    // Update audio info
    if (audioChapterTitle) audioChapterTitle.textContent = chapterLabel;
    if (audioPageInfo) audioPageInfo.textContent = `${title} of ${totalPages} pages`;

    // Update buttons - on desktop, disable when at end of spread
    if (isDesktopView()) {
      if (prevBtn) {
        prevBtn.disabled = false;
        prevBtn.classList.remove('disabled');
      }
      if (nextBtn) {
        nextBtn.disabled = currentPage >= totalPages - 2;
        nextBtn.classList.toggle('disabled', currentPage >= totalPages - 2);
      }
    } else {
      if (prevBtn) {
        prevBtn.disabled = false;
        prevBtn.classList.remove('disabled');
      }
      if (nextBtn) {
        nextBtn.disabled = currentPage === totalPages - 1;
        nextBtn.classList.toggle('disabled', currentPage === totalPages - 1);
      }
    }

    // Update chapters, quick pages, and bookmarks
    generateChapters();
    generateQuickPages();
    updateBookmarkUI();

  }

  // Update two-page spread display (desktop only)
  function updateTwoPageSpread() {
    const pageTextLeft = document.getElementById('page-text-left');
    const pageTextRight = document.getElementById('page-text-right');
    const pageContentLeft = document.getElementById('page-content-left');
    const pageContentRight = document.getElementById('page-content-right');
    const pageNumberLeftEl = document.querySelector('.page-number-left');
    const pageNumberRightEl = document.querySelector('.page-number-right');

    if (!pageTextLeft || !pageTextRight) return;

    // Left page (current page)
    const leftContent = pages[currentPage];
    const leftTitle = leftContent ? leftContent[0] : '';
    const leftHtml = leftContent ? leftContent[1] : '';

    pageTextLeft.innerHTML = leftHtml || '';
    if (pageNumberLeftEl) {
      pageNumberLeftEl.textContent = leftTitle || `Page ${currentPage + 1}`;
    }
    if (pageContentLeft) {
      pageContentLeft.classList.remove('empty-page');
    }

    // Right page (next page)
    const rightPageIndex = currentPage + 1;
    if (rightPageIndex < totalPages) {
      const rightContent = pages[rightPageIndex];
      const rightTitle = rightContent ? rightContent[0] : '';
      const rightHtml = rightContent ? rightContent[1] : '';

      pageTextRight.innerHTML = rightHtml || '';
      if (pageNumberRightEl) {
        pageNumberRightEl.textContent = rightTitle || `Page ${rightPageIndex + 1}`;
      }
      if (pageContentRight) {
        pageContentRight.classList.remove('empty-page');
      }
    } else {
      // No more pages - show empty right page
      pageTextRight.innerHTML = '<p style="text-align: center; color: #999; font-style: italic;">End of book</p>';
      if (pageNumberRightEl) {
        pageNumberRightEl.textContent = '';
      }
      if (pageContentRight) {
        pageContentRight.classList.add('empty-page');
      }
    }
  }

  // Sync Turn.js page with our currentPage tracking
  function syncTurnJsPage() {
    if (!turnJsInitialized || typeof $.fn.turn === 'undefined') return;

    try {
      const turnJsCurrentPage = $('#flipbook').turn('page');
      const expectedCurrentPage = Math.max(0, turnJsCurrentPage - 2);

      if (currentPage !== expectedCurrentPage) {
        console.warn('🔄 SYNC NEEDED: Turn.js page', turnJsCurrentPage, 'but currentPage is', currentPage);
        console.warn('🔄 Correcting currentPage to', expectedCurrentPage);
        currentPage = expectedCurrentPage;
        updateDisplay();
      }
    } catch (e) {
      console.error('Error syncing Turn.js page:', e);
    }
  }

  // ========== SECTION: Page Navigation (goToPage, Swipe, Keyboard, Tap) ==========
  function goToPage(index, skipAnimation = false) {
    if (index >= 0 && index < totalPages && index !== currentPage) {
      isOnCover = false;
      // Use Turn.js API if available
      if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
        const turnJsPage = index + 2;

        if (isNavigating) {
          console.log('[goToPage] blocked, already navigating');
          return;
        }

        isNavigating = true;
        isProgrammaticNavigation = true;

        // Close menus immediately
        chapterMenu.classList.remove('active');
        chapterMenu.style.display = 'none';
        pageMenu.classList.remove('active');
        pageMenu.style.display = 'none';

        // Execute the turn
        $('#flipbook').turn('page', turnJsPage);

        // After turn animation completes, force a full recovery
        setTimeout(function() {
          resetReaderScrollSurfaces('goToPage post');

          // Sync state to actual Turn.js page
          try {
            var actual = $('#flipbook').turn('page');
            if (actual === 1) {
              isOnCover = true;
            } else {
              isOnCover = false;
              currentPage = Math.max(0, Math.min(actual - 2, totalPages - 1));
            }
          } catch(e) {}

          updateDisplay();
          updateFontSize();
          updatePageScrolling();

          // Release locks
          isNavigating = false;
          isProgrammaticNavigation = false;
        }, NAVIGATION_COOLDOWN);

        return;
      }

      // Fallback to original animation if Turn.js not available
      const pageEl = document.getElementById('page-content');
      const bookSpread = document.querySelector('.book-spread');
      const direction = index > currentPage ? 'next' : 'prev';

      // Desktop two-page spread animation
      if (isDesktopView() && bookSpread) {
        bookSpread.classList.add('turning', 'turning-' + direction);

        setTimeout(() => {
          currentPage = index;
          updateDisplay();
          saveReadingProgress();
          syncAudioToVisibleChapter(); // Sync audio when page changes

          bookSpread.classList.remove('turning-' + direction);

          setTimeout(() => {
            bookSpread.classList.remove('turning');
          }, 300);
        }, 400);

        chapterMenu.classList.remove('active');
        chapterMenu.style.display = 'none';
        pageMenu.classList.remove('active');
        pageMenu.style.display = 'none';
        return;
      }

      // Mobile single page animation
      if (skipAnimation || !pageEl) {
        // No animation - just update
        currentPage = index;
        updateDisplay();
        saveReadingProgress();
        syncAudioToVisibleChapter(); // Sync audio when page changes
        chapterMenu.classList.remove('active');
        chapterMenu.style.display = 'none';
        pageMenu.classList.remove('active');
        pageMenu.style.display = 'none';
        return;
      }

      // Animate page turn - slower timing
      pageEl.classList.add('turning-' + direction);

      setTimeout(() => {
        // Update content while hidden
        currentPage = index;
        updateDisplay();
        saveReadingProgress();
        syncAudioToVisibleChapter(); // Sync audio when page changes

        // Remove turn class, add enter class
        pageEl.classList.remove('turning-' + direction);
        pageEl.classList.add('entering-' + direction);

        setTimeout(() => {
          pageEl.classList.remove('entering-' + direction);
        }, 600);
      }, 400);

      chapterMenu.classList.remove('active');
      chapterMenu.style.display = 'none';
      pageMenu.classList.remove('active');
      pageMenu.style.display = 'none';
    }
  }

  // Event listeners - handle two-page spread on desktop
  prevBtn.onclick = () => {
    if (isNavigating) {
      console.log('🚫 Prev button blocked - already navigating');
      return;
    }
    lastNavMethod = 'button';
    isNavigating = true;
    console.log('🔒 Navigation locked (prev button)');

    // Use Turn.js API if available
    if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
      $('#flipbook').turn('previous');
    } else if (isDesktopView()) {
      // On desktop, go back 2 pages (one spread)
      goToPage(Math.max(0, currentPage - 2));
    } else {
      goToPage(currentPage - 1);
    }

    setTimeout(() => {
      isNavigating = false;
      console.log('🔓 Navigation unlocked');
    }, NAVIGATION_COOLDOWN);
  };

  nextBtn.onclick = () => {
    if (isNavigating) {
      console.log('🚫 Next button blocked - already navigating');
      return;
    }
    lastNavMethod = 'button';
    isNavigating = true;
    console.log('🔒 Navigation locked (next button)');

    // Use Turn.js API if available
    if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
      $('#flipbook').turn('next');
    } else if (isDesktopView()) {
      // On desktop, go forward 2 pages (one spread)
      goToPage(Math.min(totalPages - 1, currentPage + 2));
    } else {
      goToPage(currentPage + 1);
    }

    setTimeout(() => {
      isNavigating = false;
      console.log('🔓 Navigation unlocked');
    }, NAVIGATION_COOLDOWN);
  };

  // Handle window resize - update display when switching between mobile/desktop
  let lastIsDesktop = isDesktopView();
  window.addEventListener('resize', function () {
    const nowIsDesktop = isDesktopView();
    if (nowIsDesktop !== lastIsDesktop) {
      lastIsDesktop = nowIsDesktop;
      updateDisplay();
    }
  });

  // Touch swipe support for mobile
  let touchStartX = 0;
  let touchEndX = 0;
  const readerContent = document.getElementById('reader-content');

  if (readerContent) {
    readerContent.addEventListener(
      'touchstart',
      function (e) {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    readerContent.addEventListener(
      'touchend',
      function (e) {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      },
      { passive: true }
    );
  }

  function handleSwipe() {
    // Prevent navigation if already navigating
    if (isNavigating) {
      console.log('🚫 Swipe blocked - already navigating');
      return;
    }

    const swipeThreshold = 50; // minimum distance for swipe
    const diff = touchStartX - touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      lastNavMethod = 'swipe';
      // Set navigation lock
      isNavigating = true;
      console.log('🔒 Navigation locked (swipe)');

      // Use Turn.js API if available
      if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
        if (diff > 0) {
          // Swipe left - next page
          $('#flipbook').turn('next');
        } else {
          // Swipe right - previous page
          $('#flipbook').turn('previous');
        }
      } else {
        const pageStep = isDesktopView() ? 2 : 1;
        if (diff > 0) {
          // Swipe left - next page(s)
          goToPage(Math.min(totalPages - 1, currentPage + pageStep));
        } else {
          // Swipe right - previous page(s)
          goToPage(Math.max(0, currentPage - pageStep));
        }
      }

      // Release navigation lock after cooldown
      setTimeout(() => {
        isNavigating = false;
        console.log('🔓 Navigation unlocked');
      }, NAVIGATION_COOLDOWN);
    }
  }

  // Keyboard navigation
  document.addEventListener('keydown', function (e) {
    // Prevent navigation if already navigating
    if (isNavigating) {
      console.log('🚫 Keyboard navigation blocked - already navigating');
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      lastNavMethod = 'keyboard';
    }

    // Use Turn.js API if available
    if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        isNavigating = true;
        console.log('🔒 Navigation locked (keyboard right)');
        $('#flipbook').turn('next');
        setTimeout(() => {
          isNavigating = false;
          console.log('🔓 Navigation unlocked');
        }, NAVIGATION_COOLDOWN);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        isNavigating = true;
        console.log('🔒 Navigation locked (keyboard left)');
        $('#flipbook').turn('previous');
        setTimeout(() => {
          isNavigating = false;
          console.log('🔓 Navigation unlocked');
        }, NAVIGATION_COOLDOWN);
      }
    } else {
      const pageStep = isDesktopView() ? 2 : 1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        isNavigating = true;
        goToPage(Math.min(totalPages - 1, currentPage + pageStep));
        setTimeout(() => {
          isNavigating = false;
        }, NAVIGATION_COOLDOWN);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        isNavigating = true;
        goToPage(Math.max(0, currentPage - pageStep));
        setTimeout(() => {
          isNavigating = false;
        }, NAVIGATION_COOLDOWN);
      }
    }
  });

  // Tap/click zones for page navigation
  // Right side tap = next page, Left side tap = previous page
  function handlePageTap(e) {
    // Let real links/buttons/inputs do their default action. Must bail BEFORE
    // preventDefault, otherwise tapping an in-book link (e.g. the Buy Now image
    // on page 684) gets its navigation cancelled. Returning early also lets the
    // click bubble to the document outside-click handler, which closes any open menus.
    if (e.target.closest('a, button, .bookmark-controls, .font-size-controls, .chapter-dropdown, .page-dropdown, .bookmarks-dropdown, input, select')) {
      return;
    }

    // Prevent event bubbling to avoid double triggers
    e.stopPropagation();
    e.preventDefault();

    // Close any open menus since stopPropagation prevents document handler
    if (chapterMenu) { chapterMenu.classList.remove('active'); chapterMenu.style.display = 'none'; }
    if (pageMenu) { pageMenu.classList.remove('active'); pageMenu.style.display = 'none'; }
    if (bookmarksMenu) { bookmarksMenu.classList.remove('active'); }

    // Prevent navigation if already navigating
    if (isNavigating) {
      console.log('🚫 Navigation blocked - already navigating');
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const centerX = rect.width / 2;

    lastNavMethod = 'tap';
    // Set navigation lock
    isNavigating = true;
    console.log('🔒 Navigation locked');

    // Right side tap -> next page, Left side tap -> previous page
    if (clickX > centerX) {
      // Right side tapped
      console.log('➡️ Tap on RIGHT side - going to NEXT page');
      if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
        $('#flipbook').turn('next');
      } else {
        const pageStep = isDesktopView() ? 2 : 1;
        goToPage(Math.min(totalPages - 1, currentPage + pageStep));
      }
    } else {
      // Left side tapped
      console.log('⬅️ Tap on LEFT side - going to PREVIOUS page');
      if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
        $('#flipbook').turn('previous');
      } else {
        const pageStep = isDesktopView() ? 2 : 1;
        goToPage(Math.max(0, currentPage - pageStep));
      }
    }

    // Release navigation lock after cooldown
    setTimeout(() => {
      isNavigating = false;
      console.log('🔓 Navigation unlocked');
    }, NAVIGATION_COOLDOWN);
  }

  // Add tap zones to reader content area
  if (readerContent) {
    readerContent.style.cursor = 'pointer';
    readerContent.addEventListener('click', handlePageTap);
  }

  //   console.log('Chapter btn:', chapterBtn);
  //   console.log('Chapter menu:', chapterMenu);
  //   console.log('Page btn:', pageBtn);
  //   console.log('Page menu:', pageMenu);

  // Chapter dropdown toggle
  if (chapterBtn && chapterMenu) {
    chapterBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      //       console.log('Chapter button clicked!');
      const isActive = chapterMenu.style.display === 'block';
      //       console.log('Chapter menu was active:', isActive);

      // Close page menu first
      if (pageMenu) {
        pageMenu.classList.remove('active');
        pageMenu.style.display = 'none';
      }

      // Toggle chapter menu using inline style
      if (isActive) {
        chapterMenu.classList.remove('active');
        chapterMenu.style.display = 'none';
      } else {
        chapterMenu.classList.add('active');
        chapterMenu.style.display = 'block';
      }
      //       console.log('Chapter menu now display:', chapterMenu.style.display);
    });
  } else {
    console.error('Chapter button or menu not found!');
  }

  // Page dropdown toggle
  if (pageBtn && pageMenu) {
    pageBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      //       console.log('Page button clicked!');

      // Close chapter menu first
      if (chapterMenu) {
        chapterMenu.classList.remove('active');
        chapterMenu.style.display = 'none';
      }

      // Toggle page menu using inline style
      const isActive = pageMenu.style.display === 'block';
      if (isActive) {
        pageMenu.classList.remove('active');
        pageMenu.style.display = 'none';
      } else {
        pageMenu.classList.add('active');
        pageMenu.style.display = 'block';
      }
    });
  } else {
    console.error('Page button or menu not found!');
  }

  // Audio controls - connect to actual audio element
  const audioElement = document.getElementById('book-audio');
  const progressFill = document.getElementById('progress-fill');
  const progressHandle = document.getElementById('progress-handle');
  const miniProgressFill = document.getElementById('mini-progress-fill');
  const timeCurrent = document.getElementById('time-current');
  const timeTotal = document.getElementById('time-total');
  const miniTimeCurrent = document.getElementById('mini-time-current');
  const miniTimeTotal = document.getElementById('mini-time-total');
  const rewindBtn = document.getElementById('rewind-btn');
  const forwardBtn = document.getElementById('forward-btn');
  const skipBackBtn = document.getElementById('skip-back-btn');
  const skipForwardBtn = document.getElementById('skip-forward-btn');

  // Audio position memory - localStorage key
  const audioPositionKey = `wishful_audio_position_${bookId}`;

  // Progress bar dragging state - shared with timeupdate listener
  let isProgressDragging = false;
  // ========== SECTION: Audio Player (Play, Seek, Progress Bars) ==========
  let isMiniProgressDragging = false;
  let lastUserSeekTime = 0;
  const SEEK_SYNC_BLOCK_DURATION = 2000; // Block sync for 2 seconds after user seek

  // Save audio position periodically
  function saveAudioPosition() {
    if (audioElement && !isNaN(audioElement.currentTime)) {
      try {
        localStorage.setItem(audioPositionKey, audioElement.currentTime.toString());
      } catch (e) {
        // localStorage not available
      }
    }
  }

  // Load saved audio position
  function loadAudioPosition() {
    try {
      const savedPosition = localStorage.getItem(audioPositionKey);
      if (savedPosition && audioElement) {
        const position = parseFloat(savedPosition);
        if (!isNaN(position) && position > 0) {
          audioElement.currentTime = position;
          //           console.log('Restored audio position:', position);
        }
      }
    } catch (e) {
      // localStorage not available
    }
  }

  // Format time helper
  function formatTime(seconds) {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // Toggle play with actual audio
  function togglePlay() {
    if (!audioElement) {
      //       console.log('No audio element found');
      return;
    }

    if (audioElement.paused) {
      audioElement.play();
      isPlaying = true;
    } else {
      audioElement.pause();
      isPlaying = false;
    }

    // Analytics: audio_play / audio_pause based on the new state.
    try {
      sendGA4Event(isPlaying ? 'audio_play' : 'audio_pause', Object.assign({}, getBookContext(), {
        page_number: currentPage + 1,
        audio_position_seconds: Math.round(audioElement.currentTime || 0)
      }));
    } catch (_) { /* analytics never blocks audio */ }

    // Update vinyl animation state - use paused class instead of removing playing class
    if (vinylMini) {
      vinylMini.classList.add('playing');
      vinylMini.classList.toggle('paused', !isPlaying);
    }
    if (vinylLarge) {
      vinylLarge.classList.add('playing');
      vinylLarge.classList.toggle('paused', !isPlaying);
    }

    // Update play icons
    const playIconMini = document.getElementById('play-icon-mini');
    const playIconLarge = document.getElementById('play-icon-large');

    if (isPlaying) {
      if (playIconMini)
        playIconMini.innerHTML =
          '<rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect>';
      if (playIconLarge)
        playIconLarge.innerHTML =
          '<rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect>';
    } else {
      if (playIconMini) playIconMini.innerHTML = '<polygon points="5 3 19 12 5 21 5 3"></polygon>';
      if (playIconLarge) playIconLarge.innerHTML = '<polygon points="5 3 19 12 5 21 5 3"></polygon>';
    }
  }

  // Audio event listeners
  if (audioElement) {
    // Track last detected chapter to avoid excessive updates
    let lastAudioChapter = null;

    // Update progress as audio plays
    audioElement.addEventListener('timeupdate', () => {
      // Skip progress bar updates while user is dragging
      if (!isProgressDragging && !isMiniProgressDragging) {
        const progress = (audioElement.currentTime / audioElement.duration) * 100;
        if (progressFill) progressFill.style.width = progress + '%';
        if (progressHandle) progressHandle.style.left = progress + '%';
        if (miniProgressFill) miniProgressFill.style.width = progress + '%';
      }

      // Always update time display
      if (timeCurrent) timeCurrent.textContent = formatTime(audioElement.currentTime);
      if (miniTimeCurrent) miniTimeCurrent.textContent = formatTime(audioElement.currentTime);

      // Save position every 5 seconds (skip while dragging)
      if (!isProgressDragging && !isMiniProgressDragging && Math.floor(audioElement.currentTime) % 5 === 0) {
        saveAudioPosition();
      }

      // ===== CHAPTER BOUNDARY DETECTION =====
      // Throttle chapter detection to avoid excessive updates
      if (!isReadOnly && manualChapters.length > 0) {
        const currentAudioChapter = getChapterAtTime(audioElement.currentTime);

        // Only update if chapter has changed
        if (currentAudioChapter && currentAudioChapter !== lastAudioChapter) {
          lastAudioChapter = currentAudioChapter;
          //           console.log('Audio crossed chapter boundary:', currentAudioChapter.name);

          // Update highlighted chapter in menu
          generateChapters();

          // Scroll text to new chapter's page
          const targetPageIndex = pages.findIndex((page) => {
            const pageText = page[0] || '';
            const pageMatch = pageText.match(/Page\s*(\d+)/i);
            return pageMatch && parseInt(pageMatch[1]) === currentAudioChapter.page;
          });

          if (targetPageIndex !== -1 && targetPageIndex !== currentPage) {
            goToPage(targetPageIndex, true); // Skip animation for smoother sync
          }
        }
      }
    });

    // Set total time when metadata loads
    audioElement.addEventListener('loadedmetadata', () => {
      if (timeTotal) timeTotal.textContent = formatTime(audioElement.duration);
      if (miniTimeTotal) miniTimeTotal.textContent = formatTime(audioElement.duration);
      // Restore saved position after metadata loads
      loadAudioPosition();
    });

    // Save position when paused
    audioElement.addEventListener('pause', saveAudioPosition);

    // Save position before page unload
    window.addEventListener('beforeunload', saveAudioPosition);

    // Handle audio end
    audioElement.addEventListener('ended', () => {
      isPlaying = false;
      // Reset vinyl to start position when audio ends
      if (vinylMini) {
        vinylMini.classList.remove('playing');
        vinylMini.classList.remove('paused');
      }
      if (vinylLarge) {
        vinylLarge.classList.remove('playing');
        vinylLarge.classList.remove('paused');
      }
      const playIconMini = document.getElementById('play-icon-mini');
      const playIconLarge = document.getElementById('play-icon-large');
      if (playIconMini) playIconMini.innerHTML = '<polygon points="5 3 19 12 5 21 5 3"></polygon>';
      if (playIconLarge) playIconLarge.innerHTML = '<polygon points="5 3 19 12 5 21 5 3"></polygon>';
      // Clear saved position when audio ends
      try {
        localStorage.removeItem(audioPositionKey);
      } catch (e) { }

      // Analytics: audio finished playing through.
      try {
        sendGA4Event('audio_complete', Object.assign({}, getBookContext(), {
          page_number: currentPage + 1,
          audio_position_seconds: Math.round(audioElement.currentTime || 0)
        }));
      } catch (_) { /* analytics never blocks audio */ }
    });

    // Analytics: asset_load_error for audio failures.
    audioElement.addEventListener('error', function () {
      try {
        sendGA4Event('asset_load_error', Object.assign({}, getBookContext(), {
          asset_url: (audioElement.currentSrc || audioElement.src || 'unknown'),
          page_number: currentPage + 1
        }));
      } catch (_) {}
    });

    // Rewind 10 seconds
    if (rewindBtn) {
      rewindBtn.onclick = () => {
        audioElement.currentTime = Math.max(0, audioElement.currentTime - 10);
      };
    }

    // Forward 10 seconds
    if (forwardBtn) {
      forwardBtn.onclick = () => {
        audioElement.currentTime = Math.min(audioElement.duration, audioElement.currentTime + 10);
      };
    }

    // Skip back 10 seconds
    if (skipBackBtn) {
      skipBackBtn.onclick = () => {
        audioElement.currentTime = Math.max(0, audioElement.currentTime - 10);
      };
    }

    // Skip forward 10 seconds
    if (skipForwardBtn) {
      skipForwardBtn.onclick = () => {
        audioElement.currentTime = Math.min(audioElement.duration, audioElement.currentTime + 10);
      };
    }

    // Click on progress bar to seek
    const progressBar = document.querySelector('.audio-progress-bar');
    const miniProgressBar = document.querySelector('.mini-progress-bar');

    // Drag/scrub functionality for expanded player progress bar
    if (progressBar) {
      function seekToPosition(e, bar) {
        const rect = bar.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        console.log('DEBUG seekToPosition:', {
          clientX: e.clientX,
          rectLeft: rect.left,
          rectWidth: rect.width,
          calculatedPercent: percent,
          finalTime: percent * audioElement.duration,
          duration: audioElement.duration
        });
        audioElement.currentTime = percent * audioElement.duration;
        // Mark user seek timestamp to block chapter-to-audio sync
        lastUserSeekTime = Date.now();
      }

      progressBar.addEventListener('mousedown', (e) => {
        isProgressDragging = true;
        progressBar.classList.add('dragging');
        seekToPosition(e, progressBar);
      });

      progressBar.addEventListener(
        'touchstart',
        (e) => {
          isProgressDragging = true;
          progressBar.classList.add('dragging');
          const touch = e.touches[0];
          seekToPosition(touch, progressBar);
        },
        { passive: true }
      );

      document.addEventListener('mousemove', (e) => {
        if (isProgressDragging && progressBar.classList.contains('dragging')) {
          seekToPosition(e, progressBar);
        }
      });

      document.addEventListener(
        'touchmove',
        (e) => {
          if (isProgressDragging && progressBar.classList.contains('dragging')) {
            const touch = e.touches[0];
            seekToPosition(touch, progressBar);
          }
        },
        { passive: true }
      );

      document.addEventListener('mouseup', () => {
        if (isProgressDragging) {
          isProgressDragging = false;
          progressBar.classList.remove('dragging');
        }
      });

      document.addEventListener('touchend', () => {
        if (isProgressDragging) {
          isProgressDragging = false;
          progressBar.classList.remove('dragging');
        }
      });

      // Simple click
      progressBar.onclick = (e) => {
        if (!isProgressDragging) {
          seekToPosition(e, progressBar);
        }
      };
    }

    // Drag/scrub functionality for mini player progress bar
    if (miniProgressBar) {
      function seekMiniToPosition(e, bar) {
        const rect = bar.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        audioElement.currentTime = percent * audioElement.duration;
        // Mark user seek timestamp to block chapter-to-audio sync
        lastUserSeekTime = Date.now();
      }

      miniProgressBar.addEventListener('mousedown', (e) => {
        isMiniProgressDragging = true;
        miniProgressBar.classList.add('dragging');
        seekMiniToPosition(e, miniProgressBar);
      });

      miniProgressBar.addEventListener(
        'touchstart',
        (e) => {
          isMiniProgressDragging = true;
          miniProgressBar.classList.add('dragging');
          const touch = e.touches[0];
          seekMiniToPosition(touch, miniProgressBar);
        },
        { passive: true }
      );

      document.addEventListener('mousemove', (e) => {
        if (isMiniProgressDragging && miniProgressBar.classList.contains('dragging')) {
          seekMiniToPosition(e, miniProgressBar);
        }
      });

      document.addEventListener(
        'touchmove',
        (e) => {
          if (isMiniProgressDragging && miniProgressBar.classList.contains('dragging')) {
            const touch = e.touches[0];
            seekMiniToPosition(touch, miniProgressBar);
          }
        },
        { passive: true }
      );

      document.addEventListener('mouseup', () => {
        if (isMiniProgressDragging) {
          isMiniProgressDragging = false;
          miniProgressBar.classList.remove('dragging');
        }
      });

      document.addEventListener('touchend', () => {
        if (isMiniProgressDragging) {
          isMiniProgressDragging = false;
          miniProgressBar.classList.remove('dragging');
        }
      });

      // Simple click
      miniProgressBar.onclick = (e) => {
        if (!isMiniProgressDragging) {
          seekMiniToPosition(e, miniProgressBar);
        }
      };
    }
  }

  if (playBtnMini) playBtnMini.onclick = togglePlay;
  if (playBtnLarge) playBtnLarge.onclick = togglePlay;

  // Get footer and divider elements for hiding when expanded
  const readerFooterEl = document.querySelector('.reader-footer');
  const readerDividerEl = document.querySelector('.reader-divider');

  if (expandAudioBtn) {
    expandAudioBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      //       console.log('Expand button clicked');
      if (audioExpanded) {
        audioExpanded.classList.add('active');
        audioExpanded.style.display = 'flex';
        // Hide footer and divider when expanded
        if (readerFooterEl) readerFooterEl.style.display = 'none';
        if (readerDividerEl) readerDividerEl.style.display = 'none';

        // Sync vinyl animation state from mini to large
        if (vinylMini && vinylLarge) {
          if (vinylMini.classList.contains('playing')) {
            vinylLarge.classList.add('playing');
          }
          if (vinylMini.classList.contains('paused')) {
            vinylLarge.classList.add('paused');
          }
        }
      }
    });
  }

  if (collapseAudioBtn) {
    collapseAudioBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      //       console.log('Collapse button clicked');
      if (audioExpanded) {
        audioExpanded.classList.remove('active');
        audioExpanded.style.display = 'none';
        // Show footer and divider when collapsed
        if (readerFooterEl) readerFooterEl.style.display = 'flex';
        if (readerDividerEl) readerDividerEl.style.display = 'flex';

        // Sync vinyl animation state from large to mini
        if (vinylLarge && vinylMini) {
          if (vinylLarge.classList.contains('playing')) {
            vinylMini.classList.add('playing');
          }
          if (vinylLarge.classList.contains('paused')) {
            vinylMini.classList.add('paused');
          }
        }
      }
    });
  }

  // Close menus when clicking outside
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.chapter-dropdown-wrapper')) {
      chapterMenu.classList.remove('active');
      chapterMenu.style.display = 'none';
    }
    if (!e.target.closest('.page-dropdown-wrapper')) {
      pageMenu.classList.remove('active');
      pageMenu.style.display = 'none';
    }
    if (!e.target.closest('.bookmarks-dropdown-wrapper') && !e.target.closest('.bookmark-btn')) {
      if (bookmarksMenu) bookmarksMenu.classList.remove('active');
    }
  });

  // Keyboard navigation (duplicate section - removing)
  // Handled above with Turn.js support

  // ========== SECTION: Font Size Controls ==========
  const fontDecreaseBtn = document.getElementById('font-decrease');
  const fontIncreaseBtn = document.getElementById('font-increase');
  const fontSizeDisplay = document.getElementById('font-size-display');
  const pageTextElement = document.getElementById('page-text');

  // Font size settings (in percentage)
  const fontSizes = [75, 85, 100, 115, 130, 150];
  let currentFontSizeIndex = 0; // Start at 75%

  // Font size preference versioning:
  // bumping version forces one-time reset to new default (75%) for older sessions.
  const fontSizePrefVersion = '3';
  const fontSizePrefVersionKey = 'bookReaderFontSizeVersion';
  const savedFontSize = localStorage.getItem('bookReaderFontSize');
  const savedPrefVersion = localStorage.getItem(fontSizePrefVersionKey);

  if (savedPrefVersion === fontSizePrefVersion && savedFontSize) {
    const savedIndex = fontSizes.indexOf(parseInt(savedFontSize, 10));
    if (savedIndex !== -1) {
      currentFontSizeIndex = savedIndex;
    }
  }

  function updateFontSize() {
    const size = fontSizes[currentFontSizeIndex];
    // Base font size in px (shrunk from 12 so 100% matches old 85%).
    // On desktop this now scales with the flipbook: the books are
    // pre-paginated (one <h6> per page), so a wider page cannot pull more
    // text onto itself — without this the text stays small and the extra
    // room just becomes blank margin. 800 is the width the flipbook used
    // to be locked at, so 800px wide == the original sizing exactly.
    // Mobile is untouched: it always uses the original 10.2.
    var baseFontSize = 10.2;
    try {
      if (turnJsInitialized && typeof $.fn.turn !== 'undefined') {
        var _fbSize = $('#flipbook').turn('size');
        if (_fbSize && _fbSize.width) {
          if (isDesktopView()) {
            baseFontSize = 10.2 * Math.max(1, Math.min(2.2, _fbSize.width / 800));
          } else if (window.innerWidth >= 768) {
            // Tablet. Scaled against the PHONE proportion, which is the one
            // that already reads correctly: a phone page is roughly 380px wide
            // at a 10.2px base, i.e. about 1px of text per 37px of page.
            //
            // (An earlier version divided by 760 — the old width CAP. The
            // flipbook never actually rendered that wide, it was 600, so the
            // multiplier came out near 1 and the text barely moved.)
            //
            // At a ~925px portrait page this gives a base near 23px, holding
            // the same text-to-page ratio a phone has. Floor of 1 means it can
            // never render smaller than the original.
            baseFontSize = 10.2 * Math.max(1, Math.min(2.6, _fbSize.width / 400));
          }
        }
      }
    } catch (e) {}
    const newSize = (baseFontSize * size) / 100;

    // Apply to fallback mode element
    if (pageTextElement) {
      pageTextElement.style.fontSize = newSize + 'px';
    }

    // Apply to Turn.js pages
    const turnJsPages = document.querySelectorAll('#flipbook .page-content');
    turnJsPages.forEach(page => {
      page.style.setProperty('font-size', newSize + 'px', 'important');
    });

    // Apply to cover page content
    const coverContent = document.querySelectorAll('.cover-content, .cover-content h1, .cover-content p');
    coverContent.forEach(el => {
      const tagName = el.tagName.toLowerCase();
      if (tagName === 'h1') {
        el.style.fontSize = (newSize * 2.13) + 'px'; // h1 is ~32px at 100% (32/15 = 2.13)
      } else if (tagName === 'p') {
        el.style.fontSize = (newSize * 1.07) + 'px'; // p is ~16px at 100% (16/15 = 1.07)
      } else {
        el.style.fontSize = newSize + 'px';
      }
    });

    if (fontSizeDisplay) {
      fontSizeDisplay.textContent = size + '%';
    }

    // Save to localStorage
    localStorage.setItem('bookReaderFontSize', size);
    localStorage.setItem(fontSizePrefVersionKey, fontSizePrefVersion);

    // Update button states
    if (fontDecreaseBtn) {
      fontDecreaseBtn.style.opacity = currentFontSizeIndex === 0 ? '0.5' : '1';
    }
    if (fontIncreaseBtn) {
      fontIncreaseBtn.style.opacity = currentFontSizeIndex === fontSizes.length - 1 ? '0.5' : '1';
    }

    updatePageScrolling();
  }

  function updatePageScrolling() {
    var allow = fontSizes[currentFontSizeIndex] > 100;
    var els = document.querySelectorAll('#flipbook .page-content');
    for (var i = 0; i < els.length; i++) {
      els[i].style.setProperty('overflow-y', allow ? 'auto' : 'hidden', 'important');
      if (!els[i].__scrollGuardAttached) {
        els[i].__scrollGuardAttached = true;
        els[i].addEventListener('scroll', onPageContentScroll, { passive: true });
      }
    }
  }

  // Snap .page-content scrollTop back to 0 the moment a non-zero value lands,
  // but only when the user has scrolling intentionally disabled (font <=100%).
  // iOS Safari re-applies a saved scrollTop when a backgrounded tab becomes
  // visible after a multi-tab session restore. The existing visibilitychange
  // 4-pass reset can lose the race; this catches the restoration the instant
  // the scroll value lands, regardless of when iOS chooses to apply it.
  function onPageContentScroll() {
    if (fontSizes[currentFontSizeIndex] > 100) return;
    if (this.scrollTop !== 0) {
      this.scrollTop = 0;
    }
  }



  if (fontDecreaseBtn) {
    fontDecreaseBtn.onclick = () => {
      if (currentFontSizeIndex > 0) {
        currentFontSizeIndex--;
        updateFontSize();
      }
    };
  }

  if (fontIncreaseBtn) {
    fontIncreaseBtn.onclick = () => {
      if (currentFontSizeIndex < fontSizes.length - 1) {
        currentFontSizeIndex++;
        updateFontSize();
      }
    };
  }

  // Initialize font size
  updateFontSize();

  // ========== SECTION: Restore Progress & Initialization ==========
  /**
   * Restore saved position on page load
   */
  function restoreProgress() {
    try {
      const savedProgress = localStorage.getItem(`wishful_detailed_progress_${bookId}`);

      if (!savedProgress) {
        return;
      }

      const progress = JSON.parse(savedProgress);

      // Validate saved data
      if (progress.bookId !== bookId) {
        console.warn('[Progress] Book ID mismatch, ignoring saved progress');
        return;
      }

      isRestoringProgress = true;

      // Restore page position
      if (typeof progress.page === 'number' && progress.page >= 0 && progress.page < totalPages) {
        goToPage(progress.page);

        // Analytics: reader picked up where they left off.
        try {
          const resumedPage = progress.page + 1; // 1-indexed
          const resumedPct = Math.round((resumedPage / totalPages) * 100);
          sendGA4Event('reading_resumed', Object.assign({}, getBookContext(), {
            resumed_page: resumedPage,
            percentage: resumedPct
          }));
        } catch (_) { /* analytics never blocks restore */ }
      }

      // Restore chapter and audio position if available
      if (!isReadOnly && progress.chapterIndex >= 0 && progress.chapterIndex < manualChapters.length) {
        setTimeout(() => {
          seekToChapter(progress.chapterIndex);

          // Fine-tune audio position if saved
          if (audioElement && typeof progress.audioTime === 'number' && progress.audioTime > 0) {
            setTimeout(() => {
              audioElement.currentTime = progress.audioTime;
            }, 100);
          }

          isRestoringProgress = false;
        }, 500);
      } else {
        isRestoringProgress = false;
      }

    } catch (e) {
      isRestoringProgress = false;
      console.warn('[Progress] Error restoring progress:', e);
    }
  }

  let bookReadyDispatched = false;
  function dispatchBookReady() {
    if (bookReadyDispatched) return;
    bookReadyDispatched = true;
    // Signal that the book is fully ready so the mode-selection loader can fade out.
    window.dispatchEvent(new CustomEvent('wishful:bookReady'));
  }

  async function doInitialize() {
    await waitForReaderVisible();
    const turnJsSuccess = initTurnJs();
    if (turnJsSuccess) {
      updateFontSize();
    }
    updateDisplay();
    setTimeout(() => {
      restoreProgress();
    }, 100);
    scheduleReaderSurfaceReset('Initial restore settle', {
      startDelay: 650
    });
    dispatchBookReady();
  }

  function initializeBookReader() {
    if (typeof jQuery === 'undefined') {
      console.error('[Init] jQuery not available');
      try {
        sendGA4Event('book_open_error', Object.assign({}, getBookContext(), {
          error_message: 'jquery_not_available'
        }));
      } catch (_) {}
      updateDisplay();
      dispatchBookReady();
      return;
    }

    if (typeof $.fn.turn === 'undefined') {
      console.warn('[Init] Turn.js not ready, retrying...');
      let retries = 0;
      const retryInterval = setInterval(() => {
        retries++;
        if (typeof $.fn.turn !== 'undefined') {
          clearInterval(retryInterval);
          console.log('[Init] Turn.js ready after', retries, 'retries');
          doInitialize();
        } else if (retries >= 15) {
          clearInterval(retryInterval);
          console.error('[Init] Turn.js failed to load, using fallback');
          try {
            sendGA4Event('book_open_error', Object.assign({}, getBookContext(), {
              error_message: 'turnjs_failed_to_load'
            }));
          } catch (_) {}
          updateDisplay();
          setTimeout(() => restoreProgress(), 100);
          dispatchBookReady();
        }
      }, 200);
      return;
    }

    doInitialize();
  }

  // Wait for jQuery to be ready before initializing
  if (typeof jQuery !== 'undefined') {
    jQuery(document).ready(function () {
      initializeBookReader();
    });
  } else {
    // Fallback if jQuery is not loaded yet
    console.warn('jQuery not loaded, waiting...');
    window.addEventListener('load', function () {
      if (typeof jQuery !== 'undefined') {
        initializeBookReader();
      } else {
        console.error('jQuery failed to load, using fallback mode');
        updateDisplay();
        setTimeout(() => {
          restoreProgress();
        }, 100);
      }
    });
  }

  // ========== SECTION: Content Protection & Visibility Recovery ==========
  (function() {
    var customerTags = window.__customerTags || [];
    document.querySelectorAll('.protected-content').forEach(function(el) {
      var requiredTag = el.getAttribute('data-tag');
      var encoded = el.getAttribute('data-content');
      if (!requiredTag || !encoded) return;
      // CASE SENSITIVE tag check
      if (customerTags.indexOf(requiredTag) !== -1) {
        try {
          var decoded = decodeURIComponent(escape(atob(encoded)));
          el.textContent = decoded;
          el.style.cssText = '';
          el.className = '';
        } catch(e) { console.warn('Decode failed:', e); }
      } else {
        el.style.cssText = 'background:#f5f5f5;color:#999;padding:4px 8px;border-radius:4px;font-style:italic';
        el.textContent = '\uD83D\uDD12 This content requires a membership to view.';
      }
    });
  })();

  // Recovery: reset the known scroll surfaces when Safari restores or returns
  // to the tab, without changing the flipbook size.
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible' && turnJsInitialized) {
      setTimeout(function() {
        try {
          scheduleReaderSurfaceReset('Visibility recovery', {
            includeLatePass: true
          });
        } catch(e) {}
      }, 0);
    }
    // Analytics: tab going hidden is a reliable session-end signal.
    if (document.visibilityState === 'hidden') {
      try { endReadingSession(); } catch(_) {}
    }
  });

  window.addEventListener('pageshow', function(event) {
    if (event.persisted && turnJsInitialized) {
      scheduleReaderSurfaceReset('PageShow recovery');
    }
  });

  // ===== Analytics: reading_session_end =====
  // Fires at most once per session. Uses GA4's beacon transport so the request
  // survives the tab closing. Also flushes a final page_dwell for the page the
  // user was on when they left.
  function endReadingSession() {
    if (sessionEndSent) return;
    sessionEndSent = true;
    try {
      const pageNum = currentPage + 1;
      const percentage = totalPages > 0 ? Math.round((pageNum / totalPages) * 100) : 0;
      const dwellSeconds = Math.max(0, Math.round((Date.now() - pageEnterTime) / 1000));
      const ctx = getBookContext();

      // Final page_dwell for the page being left (direction unknown — call it 'exit').
      sendGA4Event('page_dwell', Object.assign({}, ctx, {
        page_number: pageNum,
        dwell_seconds: dwellSeconds,
        direction: 'exit',
        total_pages: totalPages
      }));

      sendGA4Event('reading_session_end', Object.assign({}, ctx, {
        last_page: pageNum,
        max_page_reached: maxPageReached,
        percentage: percentage,
        pages_viewed_in_session: pagesViewedInSession,
        session_duration_seconds: Math.max(0, Math.round((Date.now() - sessionStartTime) / 1000)),
        transport_type: 'beacon'
      }));
    } catch (_) { /* never throw out of an unload handler */ }
  }

  // ===== TEMPORARY: on-screen sizing readout =====
  // Two ways in, because the mode-selection page navigates and drops any
  // query string before the reader ever loads:
  //   1. TRIPLE-TAP the page counter in the footer (e.g. "196/664")
  //   2. ?debug=1 on the reader URL, if you can reach it directly
  // Tapping again hides it. Does nothing otherwise, so it is safe to leave
  // in place. Remove this whole block once the sizing is settled.
  function showDebugReadout() {
    var existing = document.getElementById('wt-debug-readout');
    if (existing) { existing.remove(); return; }

    try {
      var rc = document.getElementById('reader-content');
      var fb = document.getElementById('flipbook');
      var nav = document.querySelector('.reader-nav-bar');
      var ft = document.querySelector('.reader-footer');
      var dv = document.querySelector('.reader-divider');
      var st = rc ? window.getComputedStyle(rc) : null;
      var turnSize = null;
      try { turnSize = $('#flipbook').turn('size'); } catch (_) {}

      var lines = [
        'inner: ' + window.innerWidth + ' x ' + window.innerHeight,
        'desktop(>=1024): ' + (window.innerWidth >= 1024) + '  tablet(>=768): ' + (window.innerWidth >= 768),
        'rc client: ' + (rc ? rc.clientWidth + ' x ' + rc.clientHeight : 'n/a'),
        'rc padding: ' + (st ? st.paddingTop + '/' + st.paddingRight + '/' + st.paddingBottom + '/' + st.paddingLeft : 'n/a'),
        'rc top: ' + (rc ? Math.round(rc.getBoundingClientRect().top) : 'n/a'),
        'navbar: ' + (nav ? nav.offsetHeight : 'n/a') +
          '  footer: ' + (ft ? ft.offsetHeight : 'n/a') +
          '  divider: ' + (dv ? dv.offsetHeight : 'n/a'),
        'flipbook offset: ' + (fb ? fb.offsetWidth + ' x ' + fb.offsetHeight : 'n/a'),
        'turn(size): ' + (turnSize ? turnSize.width + ' x ' + turnSize.height : 'n/a'),
        'computed: ' + JSON.stringify(getFlipbookDimensions())
      ];

      var box = document.createElement('div');
      box.id = 'wt-debug-readout';
      box.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:99999;' +
        'background:rgba(0,0,0,0.9);color:#0f0;font:13px/1.55 monospace;' +
        'padding:12px 14px;white-space:pre;overflow:auto;max-height:55vh;' +
        '-webkit-user-select:text;user-select:text;';
      box.textContent = lines.join('\n');
      document.body.appendChild(box);
    } catch (e) {}
  }

  if (urlParams.get('debug') === '1') {
    setTimeout(showDebugReadout, 2500);
  }

  // Triple-tap the footer page counter to toggle the readout.
  (function () {
    var counter = document.getElementById('footer-page-count');
    if (!counter) return;
    var taps = 0;
    var tapTimer = null;
    counter.addEventListener('click', function (e) {
      e.stopPropagation();
      taps++;
      clearTimeout(tapTimer);
      if (taps >= 3) { taps = 0; showDebugReadout(); return; }
      tapTimer = setTimeout(function () { taps = 0; }, 800);
    });
  })();

  // pagehide is the most reliable "tab is going away" signal on mobile.
  window.addEventListener('pagehide', endReadingSession);

  // ===== Analytics: asset_load_error for in-book images =====
  // <img> error events do NOT bubble, so capture-phase listen on the flipbook.
  // Catches any in-book image that fails to load (broken src, B2 hiccup, etc).
  (function () {
    const fb = document.getElementById('flipbook');
    if (!fb) return;
    fb.addEventListener('error', function (e) {
      if (!e.target || e.target.tagName !== 'IMG') return;
      try {
        sendGA4Event('asset_load_error', Object.assign({}, getBookContext(), {
          asset_url: e.target.currentSrc || e.target.src || 'unknown',
          page_number: currentPage + 1
        }));
      } catch (_) {}
    }, true);
  })();

});
